import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { _T, C, getTheme } from '../../shared/theme';
import { LANGUAGES, t } from '../../shared/i18n';
import { s_PB, s_GB, s_GC } from '../../shared/styles';
import { useStorage, useDbField } from '../../shared/hooks';
import { syncReminderPrefs, subscribeToPush, unsubscribeFromPush, deletePost, deleteAccount } from '../../shared/dbService';
import { isNative } from '../../shared/nativePush';
import { GROWTH_CATEGORIES } from '../goals/constants';
import { EXERCISES } from '../exercises/constants';
import { calcLoadAdjusted, loadGameHistory, getInitials, timeAgo } from '../../shared/utils';
import { playSound } from '../../shared/soundEngine';
import ArcGauge from '../../components/ui/ArcGauge';
import PhotoLightbox from '../../components/ui/PhotoLightbox';
import SoundThemePicker from '../../components/ui/SoundThemePicker';
import HeroBannerEditor from './HeroBannerEditor';
import FocusStats from './FocusStats';
import SleepMoodWeekCard from '../sleep-mood/SleepMoodWeekCard';
import SocialStatsCard from '../social/SocialStatsCard';
import ProfileBadges from '../social/ProfileBadges';
import DailyPulse from './DailyPulse';
import WeeklyIntention from '../onboarding/WeeklyIntention';

const BASE = process.env.REACT_APP_API_URL || '/api';

const ALL_GAME_IDS = ["memory","reaction","math","sequence","stroop","focustap","oddsymbol","directionflash","peripheralflash","numchain","wordmemory"];
const GAME_META = {
  memory:{icon:"🧠",label:"Memory",labelNL:"Geheugen",color:"#7B6FD0"},
  reaction:{icon:"⚡",label:"Reaction",labelNL:"Reactietijd",color:"#D4873A"},
  math:{icon:"➕",label:"Math",labelNL:"Rekenen",color:"#5B9ED0"},
  sequence:{icon:"🔢",label:"Sequence",labelNL:"Reeks",color:"#2A9E82"},
  stroop:{icon:"🌈",label:"Stroop",labelNL:"Stroop",color:"#C06090"},
  focustap:{icon:"🎯",label:"Focus Tap",labelNL:"Focus Tap",color:"#4AC87A"},
  oddsymbol:{icon:"🔍",label:"Odd Symbol",labelNL:"Vreemd Symbool",color:"#D4873A"},
  directionflash:{icon:"🧭",label:"Direction Flash",labelNL:"Richting Flash",color:"#C06090"},
  peripheralflash:{icon:"👁",label:"Peripheral Flash",labelNL:"Perifeer",color:"#C8A84B"},
  numchain:{icon:"🔗",label:"Number Chain",labelNL:"Nummerketen",color:"#5B9ED0"},
  wordmemory:{icon:"📝",label:"Word Memory",labelNL:"Woordgeheugen",color:"#7B6FD0"},
};

function Dashboard({lang,weekIntention,setWeekIntention,weekAgenda,datedAgenda,onNav,userName,currentUserId,themeId,setThemeId,onResetWeek,setLang,onOpenLangPicker,allUsers,onOpenProfile,friends,friendRequests,onFriendRequest,onAcceptFriend,onDeclineFriend,myBannerColor,setMyBannerColor,myAccentColor,setMyAccentColor,myPosts,setMyPosts,activeSessions,onRejoinSession,onStopSession,conversations,license,walletBalance,bizWalletBalance,onOpenBusiness,businessId,bizDisplayName,bizDisplayLogo}) {
  const nl = lang === "nl";
  const [intentionPickerOpen, setIntentionPickerOpen] = useState(false);
  const [subTab, setSubTab] = useState("dashboard");
  const [name, setName] = useStorage("sl_name", "");
  const [edit, setEdit] = useState(false);
  const [tmp, setTmp] = useState("");
  const [reminderOffset, setReminderOffset] = useStorage("sl_reminder_offset_mins", 15);
  const [tzOffset, setTzOffset] = useStorage("sl_tz_offset_mins", 60);
  const [notifPerm, setNotifPerm] = useState(() => 'Notification' in window ? Notification.permission : 'unsupported');
  const [pushEnabled, setPushEnabled] = useState(() => localStorage.getItem('sl_push_enabled') !== 'false');
  const [pushSubMissing, setPushSubMissing] = useState(false);
  const [showPushPopup, setShowPushPopup] = useState(false);
  const [acctDeleteConfirm, setAcctDeleteConfirm] = useState(false);
  const [acctDeleteLoading, setAcctDeleteLoading] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [restingFriends, setRestingFriends] = useState([]);
  const [sleepLog] = useStorage("sl_sleep_log", []);
  const [moodLog] = useStorage("sl_mood_log", []);
  const [exHistory] = useStorage("sl_ex_history", []);
  const [licenseLocal, setLicense] = useState(license || "freemium");
  const [gmExpanded, setGmExpanded] = useState(false);
  const [coachInsightOpen, setCoachInsightOpen] = useState(false);
  // Extra posts fetched directly from DB — used as a fallback for email-auth
  // Fetch personal posts directly from the posts table so images and resonance data are included.
  const [extraDbPosts, setExtraDbPosts] = useState([]);
  useEffect(() => {
    const uid = localStorage.getItem('sl_user_id');
    if (!uid) return;
    // Load license from user profile
    fetch(`${BASE}/users/${uid}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.sl_license) setLicense(data.sl_license); })
      .catch(() => {});
    // Load posts with images and resonance stats from the posts table
    fetch(`${BASE}/posts/${uid}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data) && data.length > 0) setExtraDbPosts(data); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Background push subscription check — runs once on mount.
  // If push is enabled in the browser but not registered on the server, re-subscribe
  // automatically and show a popup/banner prompting the user to enable if not granted.
  useEffect(() => {
    const uid = localStorage.getItem('sl_user_id');
    if (!uid) return;
    // On native (Android/iOS) push is handled by Capacitor nativePush.js — skip web check entirely
    if (isNative()) return;
    const perm = 'Notification' in window ? Notification.permission : 'unsupported';
    // If permission not granted yet and push hasn't been explicitly disabled, show popup
    if (perm !== 'granted' && localStorage.getItem('sl_push_enabled') !== 'false') {
      setShowPushPopup(true);
      return;
    }
    if (perm !== 'granted') return;
    // Permission is granted — check if we have a server-side subscription
    fetch(`${BASE}/push/${uid}/status`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.web_subscriptions === 0) {
          // No subscription on server — re-subscribe silently
          subscribeToPush().then(() => {
            // Check again after re-subscribing
            fetch(`${BASE}/push/${uid}/status`)
              .then(r => r.ok ? r.json() : null)
              .then(d => { if (d && d.web_subscriptions === 0) setPushSubMissing(true); })
              .catch(() => {});
          });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch resting friends every 2 minutes
  useEffect(() => {
    if (!currentUserId || !(friends||[]).length) return;
    const load = () => {
      fetch(`${BASE}/users?exclude=${encodeURIComponent(currentUserId)}`)
        .then(r => r.ok ? r.json() : [])
        .then(users => {
          const resting = (users||[]).filter(u => (friends||[]).includes(u.id) && u.isResting);
          setRestingFriends(resting);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [tlPosts, setTlPosts] = useStorage("sl_timeline", []);

  // Share popup state
  const [showShare, setShowShare] = useState(false);
  const [shareTxt, setShareTxt] = useState("");
  const [shareImg, setShareImg] = useState(null);
  const [sharePublic, setSharePublic] = useState(true);
  const [shareFeel, setShareFeel] = useState(null);
  const fileRef = useRef(null);

  const FEELINGS = [
    { emoji: '🔥', label: 'Motivated', labelNL: 'Gemotiveerd' },
    { emoji: '💪', label: 'Proud', labelNL: 'Trots' },
    { emoji: '😌', label: 'Calm', labelNL: 'Kalm' },
    { emoji: '🧘', label: 'Focused', labelNL: 'Gefocust' },
    { emoji: '🎉', label: 'Excited', labelNL: 'Enthousiast' },
    { emoji: '😰', label: 'Struggling', labelNL: 'Aan het worstelen' },
    { emoji: '😔', label: 'Tired', labelNL: 'Moe' },
    { emoji: '❤️', label: 'Grateful', labelNL: 'Dankbaar' },
  ];

  // Delete confirm state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  // Resonance stats modal
  const [resonancePost, setResonancePost] = useState(null); // { id, feeling }
  const [resonanceData, setResonanceData] = useState([]);
  const [resonanceLoading, setResonanceLoading] = useState(false);

  const openResonanceModal = (item) => {
    setResonancePost(item);
    setResonanceData([]);
    setResonanceLoading(true);
    fetch(`${BASE}/posts/${currentUserId}/${item.id}/resonance`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setResonanceData(Array.isArray(data) ? data : []); setResonanceLoading(false); })
      .catch(() => setResonanceLoading(false));
  };

  const handleDeletePost = (item) => {
    setTlPosts(prev => prev.filter(p => p.id !== item.id));
    setMyPosts(prev => prev.filter(p => p.id !== item.id));
    setExtraDbPosts(prev => prev.filter(p => p.id !== item.id));
    deletePost(item.id);
    setDeleteConfirm(null);
  };

  const saveReminderPrefs = (off, tz) => { syncReminderPrefs(off, tz); };
  const enablePush = async () => {
    if (!('Notification' in window)) return;
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p === 'granted') {
      localStorage.setItem('sl_push_enabled', 'true');
      setPushEnabled(true);
      await subscribeToPush();
    }
  };
  const togglePush = async () => {
    if (notifPerm === 'denied') return;
    if (notifPerm !== 'granted') { await enablePush(); return; }
    if (pushEnabled) {
      localStorage.setItem('sl_push_enabled', 'false');
      setPushEnabled(false);
      await unsubscribeFromPush();
    } else {
      localStorage.setItem('sl_push_enabled', 'true');
      setPushEnabled(true);
      await subscribeToPush();
    }
  };

  const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
  const todayDateKey = new Date().toISOString().slice(0, 10);
  const todayItems = [...(weekAgenda[todayIdx] || []), ...((datedAgenda || {})[todayDateKey] || [])];
  const { mental, physical, balance, rest, burnout, avgSleepScore, moodScore, sleepDaysLogged } = calcLoadAdjusted(todayItems, sleepLog, moodLog);
  const bc = burnout === "high" ? C.red : burnout === "medium" ? C.orange : C.teal;
  const burnoutPct = burnout === "high" ? 85 : burnout === "medium" ? 55 : Math.max(15, Math.round((mental + physical) / 2));
  const hour = new Date().getHours();
  const greet = hour < 12 ? t("greet.morning", lang) : hour < 18 ? t("greet.afternoon", lang) : t("greet.evening", lang);
  const incoming = (friendRequests || []).filter(r => r.to === currentUserId && r.status === "pending");
  const themes = [
    {id:"gold",       icon:"✨",label:"Gold",       grad:"linear-gradient(135deg,#C8A84B,#E4C97A)"},
    {id:"teal",       icon:"🌊",label:"Ocean",      grad:"linear-gradient(135deg,#1a8fa0,#2ABFCF)"},
    {id:"purple",     icon:"🔮",label:"Cosmos",     grad:"linear-gradient(135deg,#7B6FD0,#A99EE8)"},
    {id:"green",      icon:"🌿",label:"Forest",     grad:"linear-gradient(135deg,#2A9E60,#4AC87A)"},
    {id:"sun",        icon:"☀️",label:"Sun",        grad:"linear-gradient(135deg,#E8922A,#F5C842)",   premium:true},
    {id:"psychic",    icon:"🧿",label:"Psychic",    grad:"linear-gradient(135deg,#9B30FF,#D580FF)",   premium:true},
    {id:"paranormal", icon:"👻",label:"Paranormal", grad:"linear-gradient(135deg,#00C9A7,#80FFE8)",   premium:true},
    {id:"neon",       icon:"🌸",label:"Neon",       grad:"linear-gradient(135deg,#FF2D87,#FF9F00)",   gameLvl:5},
    {id:"midnight",   icon:"🌌",label:"Midnight",   grad:"linear-gradient(135deg,#0066FF,#00CFFF)",   gameLvl:10},
    {id:"crimson",    icon:"🔴",label:"Crimson",    grad:"linear-gradient(135deg,#C0392B,#FF6B6B)",   gameLvl:20},
    {id:"nebula",     icon:"🌠",label:"Nebula",     grad:"linear-gradient(135deg,#7B2FBE,#FF8C42)",   gameLvl:30},
  ];

  // Highest game level achieved across all games
  const maxGameLevel = (() => {
    try {
      const levels = JSON.parse(localStorage.getItem('sl_game_levels') || '{}');
      return Math.max(0, ...Object.values(levels).map(v => (v && v.level) ? v.level : 0));
    } catch { return 0; }
  })();

  // Account management state (profile tab)
  const authMethod = localStorage.getItem('sl_auth_method') || 'google';
  const isEmailUser = authMethod === 'email';
  const [chgEmailOpen,    setChgEmailOpen]    = useState(false);
  const [chgPwdOpen,      setChgPwdOpen]      = useState(false);
  const [chgEmailPwd,     setChgEmailPwd]     = useState('');
  const [chgNewEmail,     setChgNewEmail]     = useState('');
  const [chgOldPwd,       setChgOldPwd]       = useState('');
  const [chgNewPwd,       setChgNewPwd]       = useState('');
  const [acctMsg,         setAcctMsg]         = useState(null); // {type:'ok'|'err', text}
  const [acctLoading,     setAcctLoading]     = useState(false);
  const [phoneNum,        setPhoneNum]        = useDbField('sl_phone', '');
  const [phoneEdit,       setPhoneEdit]       = useState(false);
  const [phoneLoading,    setPhoneLoading]    = useState(false);

  // Theme unlock popup
  const [themeUnlockPopup, setThemeUnlockPopup] = useState(null); // theme object

  // Contacts tab state
  const [contacts,        setContacts]        = useState([]);
  const [contactsLoaded,  setContactsLoaded]  = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null); // contact detail sheet
  const [editingContact,  setEditingContact]  = useState(null); // { name, email, phone } draft
  const [contactSaving,   setContactSaving]   = useState(false);
  const [carouselIdx,     setCarouselIdx]     = useState(0);
  const carouselTouch = useRef(null);
  const [contactSearch,   setContactSearch]   = useState('');

  useEffect(() => {
    const timer = setInterval(() => setCarouselIdx(i => (i + 1) % 3), 3200);
    return () => clearInterval(timer);
  }, []);

  const loadContacts = () => {
    if (!currentUserId) return;
    setContactsLoading(true);
    fetch(`${BASE}/contacts?uid=${encodeURIComponent(currentUserId)}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setContacts(Array.isArray(data) ? data : []); setContactsLoaded(true); setContactsLoading(false); })
      .catch(() => setContactsLoading(false));
  };

  const importPhoneContacts = async () => {
    if (!('contacts' in navigator) || !navigator.contacts?.select) {
      alert(nl ? 'Contacten importeren is niet beschikbaar op dit apparaat.' : 'Contact import is not available on this device.');
      return;
    }
    try {
      const props = ['name', 'email', 'tel'];
      const imported = await navigator.contacts.select(props, { multiple: true });
      if (!imported?.length) return;
      const batch = imported.map(c => ({
        name:  (c.name?.[0] || '').trim(),
        email: (c.email?.[0] || '').trim().toLowerCase(),
        phone: (c.tel || []).map(t => t.trim()).filter(Boolean).join(','),
      })).filter(c => c.name);
      await fetch(`${BASE}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: currentUserId, contacts: batch }),
      });
      loadContacts();
    } catch (e) {
      if (e.name !== 'AbortError') alert(nl ? 'Import mislukt.' : 'Import failed.');
    }
  };

  const deleteContact = async (id) => {
    await fetch(`${BASE}/contacts?uid=${encodeURIComponent(currentUserId)}&id=${id}`, { method: 'DELETE' });
    setContacts(prev => prev.filter(c => c.id !== id));
    setSelectedContact(null);
  };

  const saveContact = async () => {
    if (!editingContact || !selectedContact) return;
    setContactSaving(true);
    await fetch(`${BASE}/contacts`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId, id: selectedContact.id, ...editingContact }),
    });
    const updated = { ...selectedContact, ...editingContact };
    setContacts(prev => prev.map(c => c.id === selectedContact.id ? updated : c));
    setSelectedContact(updated);
    setEditingContact(null);
    setContactSaving(false);
  };

  const waInvite = async (contact) => {
    const myName = (() => { try { return JSON.parse(localStorage.getItem('sl_name')||'""'); } catch { return ''; } })();
    const baseUrl = 'https://senselifting.com';
    let token = contact.invite_token;
    if (!token) {
      try {
        const r = await fetch(`${BASE}/contacts`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: currentUserId, id: contact.id }),
        });
        const d = await r.json();
        if (d.invite_token) {
          token = d.invite_token;
          setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, invite_token: token } : c));
          if (selectedContact?.id === contact.id) setSelectedContact(p => ({ ...p, invite_token: token }));
        }
      } catch {}
    }
    const ref = token ? `?invite=${token}&ref=${currentUserId}` : `?ref=${currentUserId}`;
    const msg = nl
      ? `Hé ${contact.name}! 👋 ${myName} nodigt je uit voor Sense Lifting (mentale & fysieke training). Word vrienden: ${baseUrl}${ref}`
      : `Hey ${contact.name}! 👋 ${myName} invited you to Sense Lifting (mental & physical training). Join as friends: ${baseUrl}${ref}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Avatar — mirrors AvatarUploader fallback logic
  const [avatarImg, setAvatarImg] = useState(null);
  const [bannerImg, setBannerImg] = useState(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`sl_avatar_${currentUserId || name}`);
      if (stored) { setAvatarImg(stored); return; }
      const gPic = localStorage.getItem('sl_google_picture');
      if (gPic) setAvatarImg(gPic);
    } catch {}
  }, [currentUserId, name]);
  useEffect(() => {
    try {
      const b = localStorage.getItem('sl_banner_img');
      if (b) setBannerImg(b);
    } catch {}
  }, [currentUserId]);

  // ── Build timeline ──────────────────────────────────────────────────
  const buildTimeline = () => {
    const items = [];

    // Exercise sessions (deduplicate by exId+minute)
    const sessionMap = {};
    exHistory.forEach(entry => {
      const key = `${entry.exId}_${Math.floor(entry.date / 60000)}`;
      if (!sessionMap[key]) sessionMap[key] = { type:"exercise", exId:entry.exId, date:entry.date, feelings:entry.feelings||[], cats:[] };
      if (!sessionMap[key].cats.includes(entry.catId)) sessionMap[key].cats.push(entry.catId);
    });
    items.push(...Object.values(sessionMap));

    // Level-ups: first history entry per (catId, level > 1)
    const luMap = {};
    exHistory.forEach(h => {
      if (h.level > 1) {
        const k = `${h.catId}_${h.level}`;
        if (!luMap[k] || h.date < luMap[k]) luMap[k] = h.date;
      }
    });
    Object.entries(luMap).forEach(([k, date]) => {
      const [catId, level] = k.split('_');
      items.push({ type:"levelup", catId, level:parseInt(level,10), date });
    });

    // Game plays (last 60 days, group same-game same-day into one item)
    const since = Date.now() - 60 * 24 * 3600 * 1000;
    const gameDayMap = {};
    ALL_GAME_IDS.forEach(gid => {
      loadGameHistory(gid).filter(h => h.ts > since).forEach(h => {
        const dayKey = `${gid}_${new Date(h.ts).toDateString()}`;
        if (!gameDayMap[dayKey]) gameDayMap[dayKey] = { type:"game", gameId:gid, date:h.ts, best:h.score, count:0 };
        gameDayMap[dayKey].count++;
        if (h.score > gameDayMap[dayKey].best) gameDayMap[dayKey].best = h.score;
      });
    });
    items.push(...Object.values(gameDayMap));

    // DB posts (from GET /api/posts/:uid) have img + resonance data — index by id.
    const dbPostMap = {};
    (extraDbPosts||[]).forEach(p => { if (p && p.id != null) dbPostMap[String(p.id)] = p; });

    // Collect all posts, deduplicating by id. DB version wins over local version.
    const seenIds = new Set();
    const allPostsToAdd = [];
    const addPost = (p) => {
      if (!p || p.id == null) return;
      const key = String(p.id);
      if (seenIds.has(key)) return;
      seenIds.add(key);
      // Prefer DB data (has img + resonance) but keep local fields as fallback
      const db = dbPostMap[key];
      allPostsToAdd.push(db ? { ...p, ...db } : p);
    };

    // Add DB posts first (source of truth for img + resonance)
    (extraDbPosts||[]).forEach(addPost);
    // Add local timeline posts not already covered by DB
    (Array.isArray(tlPosts) ? tlPosts : []).forEach(addPost);
    // Add optimistic local-only posts (created before DB save completes)
    (myPosts||[]).forEach(addPost);

    allPostsToAdd.forEach(p => {
      const ts = (typeof p.id === 'number' && p.id > 1e12)
        ? p.id  // optimistic local post — id is Date.now()
        : p.time ? new Date(String(p.time).replace(' ', 'T')).getTime() : Date.now();
      items.push({ ...p, type:"post", date: ts, public: p.isPublic !== false });
    });

    return items.sort((a, b) => b.date - a.date);
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setShareImg(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submitShare = () => {
    if (!shareTxt.trim() && !shareImg) return;
    const post = { id: Date.now(), date: Date.now(), text: shareTxt.trim(), img: shareImg, public: sharePublic, feeling: shareFeel };
    setTlPosts(prev => [post, ...prev]);
    // Always persist to sl_my_posts (and DB) regardless of public/private so posts
    // survive logout/cache-clear and are visible after email re-login.
    if (setMyPosts) {
      setMyPosts(prev => [{ id: post.id, text: post.text || '📸', img: post.img, isPublic: sharePublic ? 1 : 0, time: 'just now', likes: 0, feeling: shareFeel }, ...prev]);
    }
    setShareTxt(""); setShareImg(null); setSharePublic(true); setShareFeel(null); setShowShare(false);
  };

  const fmtDay = (ts) => {
    const d = new Date(ts), today = new Date(), yest = new Date(today);
    yest.setDate(yest.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return nl ? "Vandaag" : "Today";
    if (d.toDateString() === yest.toDateString()) return nl ? "Gisteren" : "Yesterday";
    return d.toLocaleDateString(nl ? "nl-NL" : "en-US", { weekday:"short", day:"numeric", month:"short" });
  };
  const fmtTime = (ts) => new Date(ts).toLocaleTimeString(nl ? "nl-NL" : "en-US", { hour:"2-digit", minute:"2-digit" });

  // ── TABS ──────────────────────────────────────────────────────────
  const TABS = [
    { id:"dashboard", icon:"🏠", label:"Dashboard" },
    { id:"timeline",  icon:"📖", label:nl?"Tijdlijn":"Timeline" },
    { id:"contacts",  icon:"👥", label:nl?"Contacten":"Contacts" },
    { id:"profile",   icon:"👤", label:t("dash.profile",lang) },
  ];

  return (
    <>
    {/* ── Portaled modals — rendered at document.body to escape all stacking contexts ── */}
    {deleteConfirm && ReactDOM.createPortal(
      <div style={{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",zIndex:9999,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{background:_T.bg2,border:`1px solid ${_T.cardBorderBot}`,borderRadius:20,padding:"24px 20px",width:"100%",maxWidth:340,boxShadow:"0 12px 48px rgba(0,0,0,0.7)"}}>
          <div style={{fontSize:20,marginBottom:10,textAlign:"center"}}>🗑</div>
          <div style={{color:C.white,fontSize:15,fontWeight:600,textAlign:"center",marginBottom:6}}>{nl?"Bericht verwijderen?":"Delete this post?"}</div>
          <div style={{color:C.silver,fontSize:13,textAlign:"center",marginBottom:20}}>{nl?"Dit verwijdert het bericht uit je tijdlijn en de community.":"This will remove the post from your timeline and the community."}</div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>setDeleteConfirm(null)} style={{flex:1,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:12,padding:"11px",cursor:"pointer",fontSize:13}}>{nl?"Annuleren":"Cancel"}</button>
            <button onClick={()=>handleDeletePost(deleteConfirm)} style={{flex:1,background:`${C.red}18`,border:`1px solid ${C.red}66`,color:C.red,borderRadius:12,padding:"11px",cursor:"pointer",fontSize:13,fontWeight:600}}>{nl?"Verwijderen":"Delete"}</button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* ── Push permission popup ── */}
    {showPushPopup && ReactDOM.createPortal(
      <div style={{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",zIndex:9999,background:"rgba(0,0,0,0.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{background:_T.bg2,border:`1px solid ${_T.cardBorderBot}`,borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:340,boxShadow:"0 12px 48px rgba(0,0,0,0.8)"}}>
          <div style={{fontSize:32,textAlign:"center",marginBottom:12}}>🔔</div>
          <div style={{color:C.white,fontSize:16,fontWeight:700,textAlign:"center",marginBottom:8}}>{nl?"Meldingen inschakelen":"Enable notifications"}</div>
          <div style={{color:C.silver,fontSize:13,textAlign:"center",marginBottom:24,lineHeight:1.5}}>
            {nl?"Ontvang meldingen voor berichten, vriendverzoeken en groepsactiviteiten.":"Get notified about messages, friend requests and group activity."}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <button onClick={async()=>{
              // requestPermission MUST be the first operation — browsers require
              // it to run synchronously within the user-gesture handler.
              if (!('Notification' in window)) { setShowPushPopup(false); return; }
              const p = await Notification.requestPermission();
              setNotifPerm(p);
              setShowPushPopup(false);
              if (p === 'granted') {
                localStorage.setItem('sl_push_enabled', 'true');
                setPushEnabled(true);
                await subscribeToPush();
              }
            }} style={{background:`${_T.accent}22`,border:`1px solid ${_T.accent}`,color:_T.accent,borderRadius:12,padding:"13px",cursor:"pointer",fontSize:14,fontWeight:700}}>
              {nl?"Inschakelen":"Enable"}
            </button>
            <button onClick={()=>{setShowPushPopup(false);localStorage.setItem('sl_push_enabled','false');}} style={{background:"transparent",border:"none",color:C.silver,cursor:"pointer",fontSize:13,padding:"6px"}}>
              {nl?"Niet nu":"Not now"}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {resonancePost && ReactDOM.createPortal(
      <div style={{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",zIndex:9999,background:"rgba(0,0,0,0.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div style={{background:_T.bg2,border:`1px solid ${_T.cardBorderBot}`,borderRadius:20,padding:"20px",width:"100%",maxWidth:400,boxShadow:"0 12px 48px rgba(0,0,0,0.8)",maxHeight:"82vh",display:"flex",flexDirection:"column"}}>

          {/* Header */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexShrink:0}}>
            <div>
              <div style={{fontSize:10,color:_T.accent,letterSpacing:3,fontWeight:700,marginBottom:3}}>{nl?"RESONANTIE":"RESONANCE"}</div>
              {resonancePost.feeling && (
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:14,color:C.white,fontWeight:600}}>
                  <span>{resonancePost.feeling.emoji}</span>
                  <span>{nl ? resonancePost.feeling.labelNL : resonancePost.feeling.label}</span>
                </div>
              )}
            </div>
            <button onClick={()=>setResonancePost(null)} style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
          </div>

          {resonanceLoading && <div style={{textAlign:"center",color:C.silver,padding:32,fontSize:13}}>···</div>}

          {!resonanceLoading && resonanceData.length === 0 && (
            <div style={{textAlign:"center",padding:32}}>
              <div style={{fontSize:32,marginBottom:10}}>💙</div>
              <div style={{color:C.silver,fontSize:13}}>{nl?"Nog niemand heeft geresoneerd.":"Nobody has resonated yet."}</div>
            </div>
          )}

          {!resonanceLoading && resonanceData.length > 0 && (() => {
            const vals = resonanceData.map(r => Number(r.value) || 0);
            const avg = Math.round(vals.reduce((s,v) => s+v, 0) / vals.length);
            const recapWord = (v, nl) => {
              if (v >= 90) return nl ? "Volledig" : "Fully";
              if (v >= 70) return nl ? "Sterk" : "Strongly";
              if (v >= 50) return nl ? "Duidelijk" : "Clearly";
              if (v >= 30) return nl ? "Enigszins" : "Somewhat";
              return nl ? "Licht" : "Slightly";
            };
            return (
              <>
                {/* Average summary */}
                <div style={{marginBottom:16,padding:"10px 14px",background:`${_T.accent}0d`,borderRadius:12,border:`1px solid ${_T.accent}25`,flexShrink:0}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
                    <span style={{fontSize:11,color:C.silver}}>{nl?"Gemiddelde resonantie":"Average resonance"}</span>
                    <span style={{fontSize:13,color:_T.accent,fontWeight:700}}>{avg}% · {resonanceData.length} {nl?(resonanceData.length===1?"persoon":"mensen"):(resonanceData.length===1?"person":"people")}</span>
                  </div>
                  <div style={{height:6,background:`${_T.accent}20`,borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${avg}%`,background:`linear-gradient(90deg,${_T.grad1},${_T.grad2})`,borderRadius:4,transition:"width 0.6s ease"}}/>
                  </div>
                </div>

                {/* Individual rows */}
                <div style={{overflowY:"auto",flex:1}}>
                  {resonanceData.map((r, i) => {
                    const val = Number(r.value) || 0;
                    const col = r.color || _T.accent;
                    const name = r.name || (nl?"Onbekend":"Unknown");
                    const initials = name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
                    const userObj = (allUsers||[]).find(u => u.id === r.userId) || { id: r.userId, name, color: col, picture: r.picture };
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,cursor:"pointer"}}
                        onClick={() => { setResonancePost(null); if (onOpenProfile) onOpenProfile(userObj); }}>
                        {/* Avatar */}
                        <div style={{flexShrink:0}}>
                          {r.picture
                            ? <img src={r.picture} alt="" style={{width:38,height:38,borderRadius:"50%",objectFit:"cover",border:`2px solid ${col}44`}}/>
                            : <div style={{width:38,height:38,borderRadius:"50%",background:`${col}22`,border:`1.5px solid ${col}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:col}}>{initials||"?"}</div>
                          }
                        </div>
                        {/* Name + bar */}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                            <span style={{fontSize:13,color:C.white,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</span>
                            <span style={{fontSize:10,color:col,background:`${col}18`,borderRadius:8,padding:"2px 7px",fontWeight:600,marginLeft:8,flexShrink:0}}>{recapWord(val,nl)}</span>
                          </div>
                          <div style={{height:5,background:`${col}18`,borderRadius:4,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${val}%`,background:`linear-gradient(90deg,${col}88,${col})`,borderRadius:4,transition:"width 0.5s ease"}}/>
                          </div>
                        </div>
                        {/* Percentage */}
                        <div style={{fontSize:14,color:col,fontWeight:700,minWidth:42,textAlign:"right",flexShrink:0}}>{val}%</div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      </div>,
      document.body
    )}

    <div className="fadeUp">
      {/* Share experience popup — fixed at top */}
      {showShare && (
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:300,background:`${_T.bg2}f5`,backdropFilter:"blur(16px)",borderBottom:`1px solid ${_T.accent}33`,borderRadius:"0 0 24px 24px",boxShadow:"0 8px 40px rgba(0,0,0,0.6)",padding:"18px 16px 20px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <div style={{fontSize:15,color:C.white,fontWeight:700}}>📸 {nl?"Ervaring delen":"Share experience"}</div>
            <button onClick={()=>{setShowShare(false);setShareImg(null);setShareTxt("");setShareFeel(null);}} style={{background:"none",border:"none",color:C.silver,fontSize:22,cursor:"pointer",lineHeight:1}}>✕</button>
          </div>

          {/* Text input */}
          <textarea
            value={shareTxt}
            onChange={e=>setShareTxt(e.target.value)}
            placeholder={nl?"Schrijf iets over je ervaring...":"Write about your experience..."}
            style={{width:"100%",background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:12,padding:"10px 14px",color:C.white,fontSize:13,resize:"none",height:80,fontFamily:"Inter,sans-serif",outline:"none",boxSizing:"border-box",marginBottom:12}}
          />

          {/* Feeling selector */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,color:C.silver,letterSpacing:2,fontWeight:600,marginBottom:7}}>{nl?"HOE VOEL JE JE?":"HOW ARE YOU FEELING?"}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {FEELINGS.map(f => {
                const sel = shareFeel?.label === f.label;
                return (
                  <button key={f.label} onClick={()=>setShareFeel(sel?null:f)} style={{background:sel?`${_T.accent}22`:_T.bg4,border:`1px solid ${sel?_T.accent:_T.cardBorderBot}`,borderRadius:20,padding:"6px 10px",cursor:"pointer",display:"flex",alignItems:"center",gap:5,transition:"all 0.15s"}}>
                    <span style={{fontSize:14}}>{f.emoji}</span>
                    <span style={{fontSize:11,color:sel?_T.accent:C.silver,fontWeight:sel?700:400}}>{nl?f.labelNL:f.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Image preview */}
          {shareImg && (
            <div style={{position:"relative",marginBottom:12}}>
              <img src={shareImg} alt="" style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:12,border:`1px solid ${_T.cardBorderBot}`}}/>
              <button onClick={()=>setShareImg(null)} style={{position:"absolute",top:8,right:8,background:"rgba(0,0,0,0.6)",border:"none",borderRadius:"50%",width:28,height:28,color:C.white,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>
          )}

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            {/* Image upload */}
            <button onClick={()=>fileRef.current?.click()} style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:10,padding:"8px 14px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:6}}>
              📷 {nl?"Foto":"Photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImagePick} style={{display:"none"}}/>

            {/* Public / Private toggle */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:"auto"}}>
              <span style={{fontSize:11,color:C.silver}}>{sharePublic?(nl?"Publiek":"Public"):(nl?"Privé":"Private")}</span>
              <button onClick={()=>setSharePublic(p=>!p)} style={{width:44,height:24,borderRadius:12,background:sharePublic?_T.accent:_T.bg4,border:`1px solid ${sharePublic?_T.accent:_T.cardBorderBot}`,cursor:"pointer",position:"relative",transition:"background 0.2s",padding:0}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:C.white,position:"absolute",top:3,left:sharePublic?22:3,transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.4)"}}/>
              </button>
              <span style={{fontSize:16}}>{sharePublic?"🌍":"🔒"}</span>
            </div>
          </div>

          <button onClick={submitShare} disabled={!shareTxt.trim()&&!shareImg} style={{...s_PB({width:"100%"}),opacity:(!shareTxt.trim()&&!shareImg)?0.4:1}}>
            {nl?"Delen":"Post"}
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{marginBottom:14,background:`linear-gradient(135deg,${_T.bg3},${_T.bg4})`,border:`1px solid ${_T.cardBorderTop}`,borderRadius:20,padding:"16px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-30,right:-30,width:120,height:120,borderRadius:"50%",background:`${_T.accent}08`,pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {avatarImg
            ?<img src={avatarImg} alt="" style={{width:48,height:48,borderRadius:"50%",objectFit:"cover",border:`2px solid ${_T.accent}44`,flexShrink:0}}/>
            :<div style={{width:48,height:48,borderRadius:"50%",background:`linear-gradient(135deg,${_T.grad1}44,${_T.grad2}22)`,border:`2px solid ${_T.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:700,color:_T.accent,flexShrink:0}}>{(name||userName||"S").charAt(0).toUpperCase()}</div>
          }
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,color:`${C.silver}88`,marginBottom:2}}>{greet} 👋</div>
            <div style={{fontFamily:"Georgia,serif",fontSize:19,color:C.white,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name||userName||"Sense Lifter"}</div>
          </div>
          {(()=>{
            const hasCoach = license && license.startsWith('coaches');
            const hasBiz = !!businessId;
            const bizName = bizDisplayName;
            const bizLogo = bizDisplayLogo;
            const coachTier = hasCoach ? (license.split('_')[1] || 'solo') : null;
            return (
              <div style={{display:'flex',flexDirection:'column',gap:6,flexShrink:0}}>
                {hasCoach && (
                  <button onClick={()=>onNav(4)}
                    style={{background:`${_T.accent}18`,border:`1px solid ${_T.accent}44`,borderRadius:12,padding:'6px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:16}}>🎯</span>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-start'}}>
                      <span style={{fontSize:10,color:_T.accent,fontWeight:700,lineHeight:1.2}}>Coach</span>
                      <span style={{fontSize:9,color:`${_T.accent}88`,lineHeight:1}}>{coachTier.charAt(0).toUpperCase()+coachTier.slice(1)}</span>
                    </div>
                  </button>
                )}
                {hasBiz && onOpenBusiness && (
                  <button onClick={onOpenBusiness}
                    style={{background:'#7B6FD020',border:'1px solid #7B6FD055',borderRadius:12,padding:'6px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:6,maxWidth:120}}>
                    {bizLogo
                      ? <img src={bizLogo} alt="" style={{width:22,height:22,borderRadius:6,objectFit:'cover',flexShrink:0}}/>
                      : <span style={{fontSize:15,flexShrink:0}}>🏢</span>}
                    <span style={{fontSize:10,color:'#7B6FD0',fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:70}}>{bizName||'Biz'}</span>
                  </button>
                )}
              </div>
            );
          })()}
        </div>
        {/* License + wallet badge row */}
        {(()=>{
          const licStr = license || 'freemium';
          const isPaid = licStr !== 'freemium';
          const licColors = {starter:'#5B9ED0',athlete:'#2A9E82',sensei:'#D4873A',professional:'#C8A84B',individual:'#7B6FD0',freemium:'#8899BB'};
          const lColor = licColors[licStr] || '#8899BB';
          const isProfessional = licStr === 'professional';
          return (
            <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${_T.cardBorderBot}`,display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:9,background:`${lColor}20`,color:lColor,border:`1px solid ${lColor}44`,borderRadius:8,padding:'2px 8px',fontWeight:700,textTransform:'uppercase',letterSpacing:1}}>
                {licStr.charAt(0).toUpperCase()+licStr.slice(1)}
              </span>
              {isPaid && (
                <span style={{fontSize:10,color:'#8899BB',display:'flex',alignItems:'center',gap:4}}>
                  {isProfessional
                    ? (nl?'Onbeperkte credits':'Unlimited credits')
                    : businessId
                      ? <>🏢 {bizWalletBalance ?? 0} {nl?'credits':'credits'}</>
                      : <>💎 {walletBalance ?? 0} {nl?'credits':'credits'}</>
                  }
                </span>
              )}
              {weekIntention && (
                <div style={{flex:1,fontSize:11,color:`${_T.accent}bb`,fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:'right'}}>
                  "{weekIntention}"
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Sub-tab bar */}
      <div style={{display:"flex",background:_T.bg4,borderRadius:14,padding:4,marginBottom:14,gap:2}}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={()=>{playSound('tab');setSubTab(tab.id);if(tab.id==='contacts'&&!contactsLoaded)loadContacts();}} style={{flex:1,background:subTab===tab.id?`linear-gradient(135deg,${_T.grad1},${_T.grad2})`:"transparent",border:"none",borderRadius:10,padding:"8px 4px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
            <span style={{fontSize:15}}>{tab.icon}</span>
            <span style={{fontSize:9,color:subTab===tab.id?"#0a1220":C.silver,fontWeight:subTab===tab.id?700:400,whiteSpace:"nowrap"}}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── DASHBOARD TAB ── */}
      {subTab==="dashboard" && (
        <div>
          {/* Unread chat messages */}
          {(()=>{
            const unreadMsgs = Object.values(conversations||{}).reduce((total, msgs) =>
              total + (Array.isArray(msgs) ? msgs.filter(m => m.from !== currentUserId && !m.read).length : 0), 0);
            return unreadMsgs > 0 ? (
              <div onClick={()=>onNav(7)} style={{display:"flex",alignItems:"center",gap:10,background:"#5B9ED015",border:"1px solid #5B9ED055",borderRadius:14,padding:"10px 14px",marginBottom:12,cursor:"pointer"}}>
                <span style={{fontSize:20}}>💬</span>
                <div style={{flex:1}}>
                  <div style={{color:C.white,fontSize:13,fontWeight:600}}>
                    {unreadMsgs} {nl ? (unreadMsgs===1?"ongelezen bericht":"ongelezen berichten") : (unreadMsgs===1?"unread message":"unread messages")}
                  </div>
                  <div style={{color:"#5B9ED0",fontSize:11,marginTop:1}}>{nl?"Tik om te reageren →":"Tap to reply →"}</div>
                </div>
                <div style={{width:8,height:8,borderRadius:"50%",background:"#5B9ED0",boxShadow:"0 0 6px #5B9ED0"}}/>
              </div>
            ) : null;
          })()}

              {incoming.length>0 && (
            <div onClick={()=>onNav(7)} style={{display:"flex",alignItems:"center",gap:10,background:`${_T.accent}15`,border:`1px solid ${_T.accent}44`,borderRadius:14,padding:"10px 14px",marginBottom:12,cursor:"pointer"}}>
              <span style={{fontSize:20}}>👥</span>
              <div style={{flex:1}}><div style={{color:C.white,fontSize:13,fontWeight:600}}>{incoming.length} {t("dash.friend_req",lang)}{incoming.length>1?"en":""}</div></div>
              <span style={{color:_T.accent,fontSize:18}}>→</span>
            </div>
          )}

          {/* Resting friends */}
          {restingFriends.length > 0 && (
            <div style={s_GC({marginBottom:14,padding:"12px 14px"})}>
              <div style={{fontSize:10,color:"#2A9E82",letterSpacing:2,fontWeight:700,marginBottom:10}}>
                🌙 {nl?"VRIENDEN AAN HET RUSTEN":"FRIENDS RESTING"}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {restingFriends.map(u => (
                  <div key={u.id} onClick={()=>onOpenProfile&&onOpenProfile(u)} style={{display:"flex",alignItems:"center",gap:6,background:"#2A9E8210",border:"1px solid #2A9E8233",borderRadius:20,padding:"5px 10px 5px 6px",cursor:"pointer"}}>
                    {u.picture
                      ? <img src={u.picture} alt={u.name} style={{width:24,height:24,borderRadius:"50%",objectFit:"cover",border:"1px solid #2A9E8244"}}/>
                      : <div style={{width:24,height:24,borderRadius:"50%",background:`${u.color||"#2A9E82"}22`,border:`1px solid ${u.color||"#2A9E82"}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:u.color||"#2A9E82"}}>{(u.name||"?").slice(0,2).toUpperCase()}</div>
                    }
                    <span style={{fontSize:12,color:C.silver2,fontWeight:600}}>{u.name.split(" ")[0]}</span>
                    <span style={{fontSize:10,color:"#2A9E82"}}>🌙</span>
                  </div>
                ))}
              </div>
              <div style={{fontSize:10,color:`${C.silver}88`,marginTop:8,fontStyle:"italic"}}>
                {nl?"Deze vrienden rusten. Kom later terug!":"These friends are resting. Check back later!"}
              </div>
            </div>
          )}

          {/* Active game sessions card */}
          {activeSessions && activeSessions.length > 0 && (
            <div style={s_GC({marginBottom:14})}>
              <div style={{fontSize:11,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:10}}>🎮 {nl?"ACTIEVE SPELSESSIES":"ACTIVE GAME SESSIONS"}</div>
              {activeSessions.map(s => {
                const gm = Object.values(GAME_META).find(m => m.label === s.gameTitle || m.labelNL === s.gameTitle) || {icon:"🎮",label:s.gameTitle||"Game",labelNL:s.gameTitle||"Spel",color:"#C8A84B"};
                const partnerName = s.role==='inviter' ? s.joinerName : s.inviterName;
                return (
                  <div key={s.token} style={{display:"flex",alignItems:"center",gap:10,background:`${gm.color}12`,border:`1px solid ${gm.color}33`,borderRadius:12,padding:"10px 12px",marginBottom:8}}>
                    <div style={{width:38,height:38,borderRadius:10,background:`${gm.color}20`,border:`1px solid ${gm.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{gm.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{color:"#EEF3FF",fontSize:12,fontWeight:600}}>{nl?gm.labelNL:gm.label}</div>
                      {partnerName && <div style={{color:"#8899BB",fontSize:10,marginTop:1}}>vs {partnerName}</div>}
                    </div>
                    <button onClick={()=>onRejoinSession&&onRejoinSession(s)} style={s_GB({fontSize:10,padding:"5px 10px",borderRadius:8})}>
                      {nl?"Hervatten":"Rejoin"}
                    </button>
                    <button onClick={()=>onStopSession&&onStopSession(s)} style={{fontSize:10,padding:"5px 10px",borderRadius:8,background:"rgba(192,80,80,0.12)",border:"1px solid #C05050",color:"#C05050",cursor:"pointer"}}>
                      {nl?"Stop":"Stop"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* 1 · Intention — opens picker when empty, stats when set */}
          <button
            onClick={() => weekIntention ? onNav(9) : setIntentionPickerOpen(true)}
            style={{...s_GC({position:"relative",overflow:"hidden",marginBottom:14}),width:"100%",textAlign:"left",cursor:"pointer",display:"block",padding:0,border:`1.5px solid ${weekIntention?_T.accent+'33':_T.accent+'66'}`}}>
            <div style={{position:"absolute",top:0,left:0,width:3,height:"100%",background:`linear-gradient(180deg,${_T.grad1},${_T.grad2})`}}/>
            <div style={{padding:"14px 40px 14px 26px"}}>
              <div style={{fontSize:11,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:4}}>{nl?"WEEKINTENTIE":"WEEK INTENTION"}</div>
              {weekIntention
                ? <div style={{color:C.silver2,fontSize:13,lineHeight:1.6,fontStyle:"italic"}}>"{weekIntention}"</div>
                : <div style={{color:_T.accent,fontSize:13,fontWeight:500}}>{nl?"✦ Wat wil jij bereiken deze week?":"✦ What do you want to achieve this week?"}</div>}
              <div style={{fontSize:10,color:_T.accent,marginTop:6}}>
                {weekIntention ? (nl?"Bekijk groeistats →":"View growth stats →") : (nl?"Tik om in te stellen →":"Tap to set →")}
              </div>
            </div>
            <span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",color:_T.accent,fontSize:18,pointerEvents:"none"}}>›</span>
          </button>

          {/* WeeklyIntention full-screen overlay */}
          {intentionPickerOpen && (
            <div style={{position:"fixed",inset:0,zIndex:9999,overflow:"hidden",display:"flex",flexDirection:"column"}}>
              <div style={{flex:1,overflowY:"auto"}}>
                <WeeklyIntention
                  lang={lang}
                  onDone={value => { setWeekIntention(value); setIntentionPickerOpen(false); }}
                />
              </div>
              <button
                onClick={() => setIntentionPickerOpen(false)}
                style={{position:"fixed",top:14,right:16,background:"rgba(8,14,26,0.7)",border:`1px solid ${_T.cardBorderBot}`,borderRadius:20,color:C.silver,fontSize:16,cursor:"pointer",zIndex:10000,lineHeight:1,padding:"4px 9px"}}>
                ✕
              </button>
            </div>
          )}

          {/* 2 · Sense Score — the core hook (streak + readiness + challenge unified) */}
          <DailyPulse
            lang={lang}
            sleepLog={sleepLog}
            moodLog={moodLog}
            exHistory={exHistory}
            avgSleepScore={avgSleepScore}
            moodScore={moodScore}
            onNav={onNav}
          />

          {/* 3 · Quick nav — primary actions */}
          <div className="sl-dash-quicknav" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
            {[{icon:"🧘",label:t("dash.exercises",lang),tab:2,c:C.purple},{icon:"🎮",label:"Games",tab:3,c:C.teal},{icon:"🤖",label:"Coach",tab:4,c:C.orange},{icon:"🎯",label:t("nav.goals",lang),tab:5,c:_T.accent}].map(b=>(
              <button key={b.tab} onClick={()=>onNav(b.tab)} style={{...s_GC({marginBottom:0,padding:"14px 10px",cursor:"pointer",textAlign:"center"})}}>
                <div style={{fontSize:22,marginBottom:5}}>{b.icon}</div>
                <div style={{color:b.c,fontSize:12,fontWeight:600}}>{b.label}</div>
              </button>
            ))}
          </div>

          {/* Growth matrix rich card */}
          {(()=>{
            let catLevels={};try{catLevels=JSON.parse(localStorage.getItem("sl_cat_levels")||"{}");}catch{}
            const thisWeekStart=Date.now()-7*24*3600*1000;
            const totalSess=exHistory.length;
            const weekSess=exHistory.filter(h=>h.date>thisWeekStart).length;
            const catStats=GROWTH_CATEGORIES.map(cat=>{
              const lv=catLevels[cat.id]||1;
              const sessions=exHistory.filter(h=>h.catId===cat.id).length;
              const weekCount=exHistory.filter(h=>h.catId===cat.id&&h.date>thisWeekStart).length;
              const currentDef=cat.levels.find(l=>l.n===lv)||cat.levels[0];
              const nextDef=cat.levels.find(l=>l.n===lv+1);
              const sessToNext=nextDef?nextDef.sessions:currentDef.sessions;
              const progress=nextDef?Math.min((sessions%Math.max(sessToNext,1))/sessToNext*100,100):100;
              return{...cat,lv,sessions,weekCount,progress,currentDef,nextDef,sessToNext};
            });
            const mostActive=[...catStats].sort((a,b)=>b.sessions-a.sessions).find(c=>c.sessions>0);
            const highestLev=[...catStats].sort((a,b)=>b.lv-a.lv)[0];
            const totalLevels=catStats.reduce((s,c)=>s+c.lv,0);
            const maxLevels=GROWTH_CATEGORIES.length*5;
            const insight=(()=>{
              if(totalSess===0) return nl
                ?`Welkom op je Sense Lifting reis! Begin met je eerste oefening om groei in beweging te zetten. De Growth Matrix toont hoe je je ontwikkelt in 7 dimensies van bewustzijn.`
                :`Welcome to your Sense Lifting journey! Start with your first exercise to set growth in motion. The Growth Matrix shows how you develop across 7 dimensions of awareness.`;
              const parts=[];
              const benefits={focus:nl?"scherper focussen en afleidingen sneller loslaten":"focus sharper and release distractions faster",body:nl?"meer lichaamsbewustzijn en minder onbewuste spanning":"greater body awareness and less unconscious tension",intuition:nl?"intuïtieve keuzes sneller vertrouwen":"trust intuitive choices faster",time:nl?"een beter gevoel voor ritme en flow":"a better sense of rhythm and flow in your day",emotion:nl?"emotionele stabiliteit en kalmte onder druk":"emotional stability and calm under pressure",rest:nl?"dieper herstel en meer energie":"deeper recovery and more energy",social:nl?"rijkere verbindingen en meer empathie":"richer connections and greater empathy"};
              if(nl){
                parts.push(`Je hebt ${totalSess} sessie${totalSess!==1?'s':''} voltooid${weekSess>0?`, waarvan ${weekSess} deze week`:''}.`);
                if(mostActive) parts.push(`Je meest getrainde gebied is ${mostActive.label} (niveau ${mostActive.lv}) — dit helpt je om ${mostActive.currentDef?.goal?.toLowerCase()||'te groeien'}.`);
                if(mostActive&&benefits[mostActive.id]) parts.push(`Dit geeft je ${benefits[mostActive.id]}.`);
                const untrained=catStats.filter(c=>c.sessions===0);
                if(untrained.length>0) parts.push(`Tip: verken ook ${untrained[0].label} om je Sense Matrix te verbreden.`);
                else if(highestLev&&highestLev.lv<5) parts.push(`Je volgende stap: niveau ${highestLev.lv+1} in ${highestLev.label} — focus op "${highestLev.nextDef?.goal||'oefenen'}".`);
              } else {
                parts.push(`You've completed ${totalSess} session${totalSess!==1?'s':''}${weekSess>0?`, including ${weekSess} this week`:''}.`);
                if(mostActive) parts.push(`Your most-trained area is ${mostActive.labelEN} (level ${mostActive.lv}) — helping you ${mostActive.currentDef?.goalEN?.toLowerCase()||'grow'}.`);
                if(mostActive&&benefits[mostActive.id]) parts.push(`This gives you ${benefits[mostActive.id]}.`);
                const untrained=catStats.filter(c=>c.sessions===0);
                if(untrained.length>0) parts.push(`Tip: also explore ${untrained[0].labelEN} to broaden your Sense Matrix.`);
                else if(highestLev&&highestLev.lv<5) parts.push(`Your next step: level ${highestLev.lv+1} in ${highestLev.labelEN} — focus on "${highestLev.nextDef?.goalEN||'practice'}".`);
              }
              return parts.join(' ');
            })();
            return (
              <div style={{...s_GC({marginBottom:14}),padding:0,overflow:"hidden",border:`1px solid ${_T.cardBorderBot}`}}>
                {/* Card header */}
                <div style={{padding:"13px 16px",borderBottom:`1px solid ${_T.cardBorderBot}`,display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",background:gmExpanded?`${_T.accent}08`:"transparent"}} onClick={()=>setGmExpanded(e=>!e)}>
                  <div>
                    <div style={{fontSize:12,color:_T.accent,letterSpacing:3,fontWeight:600}}>🕸️ {nl?"JOUW ONTWIKKELING":"JOUW ONTWIKKELING"}</div>
                    <div style={{fontSize:10,color:C.silver,marginTop:2}}>{totalLevels}/{maxLevels} {nl?"niveaus":"levels"} · {totalSess} {nl?"sessies":"sessions"}</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <button onClick={e=>{e.stopPropagation();onNav(5);}} style={{background:`${_T.accent}15`,border:`1px solid ${_T.accent}44`,color:_T.accent,borderRadius:8,padding:"4px 10px",cursor:"pointer",fontSize:11}}>{nl?"Alles →":"All →"}</button>
                    <span style={{color:C.silver,fontSize:12}}>{gmExpanded?"▲":"▼"}</span>
                  </div>
                </div>
                {/* Category badges */}
                <div style={{padding:"12px 16px",display:"flex",flexWrap:"wrap",gap:6}}>
                  {catStats.map(cat=>(
                    <div key={cat.id} style={{display:"flex",alignItems:"center",gap:4,background:`${cat.color}12`,border:`1px solid ${cat.color}33`,borderRadius:20,padding:"4px 10px"}}>
                      <span style={{fontSize:11}}>{cat.icon}</span>
                      <span style={{fontSize:11,color:cat.color,fontWeight:700}}>Lv {cat.lv}</span>
                      {cat.weekCount>0&&<span style={{fontSize:9,color:`${cat.color}99`}}>+{cat.weekCount}</span>}
                    </div>
                  ))}
                </div>
                {/* Sense Lifting Coach — always visible */}
                <div style={{margin:"0 16px 14px",background:`${_T.accent}0D`,border:`1px solid ${_T.accent}2A`,borderRadius:14,padding:"12px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{fontSize:18}}>🦋</span>
                    <span style={{color:_T.accent,fontSize:12,fontWeight:700,letterSpacing:1}}>{nl?"SENSE LIFTING COACH":"SENSE LIFTING COACH"}</span>
                  </div>
                  <div style={{color:C.silver2,fontSize:12,lineHeight:1.85}}>{insight}</div>
                </div>
                {/* Expanded detail */}
                {gmExpanded&&(
                  <div style={{borderTop:`1px solid ${_T.cardBorderBot}`}}>
                    <div style={{padding:"14px 16px"}}>
                      {catStats.map(cat=>(
                        <div key={cat.id} style={{marginBottom:13}}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontSize:14}}>{cat.icon}</span>
                              <span style={{fontSize:12,color:C.white,fontWeight:600}}>{nl?cat.label:cat.labelEN}</span>
                              <span style={{fontSize:9,color:`${cat.color}cc`,background:`${cat.color}18`,padding:"1px 6px",borderRadius:6,fontWeight:700}}>Lv {cat.lv}</span>
                              {cat.weekCount>0&&<span style={{fontSize:9,color:C.teal}}>+{cat.weekCount}x</span>}
                            </div>
                            <span style={{fontSize:10,color:C.silver}}>{cat.sessions}x {nl?"totaal":"total"}</span>
                          </div>
                          <div style={{fontSize:10,color:`${C.silver}88`,marginBottom:4,fontStyle:"italic"}}>{nl?cat.currentDef?.goal:cat.currentDef?.goalEN}</div>
                          <div style={{height:4,background:_T.bg4,borderRadius:2,overflow:"hidden",marginBottom:2}}>
                            <div style={{width:`${cat.progress}%`,height:"100%",background:`linear-gradient(90deg,${cat.color}66,${cat.color})`,borderRadius:2,transition:"width 0.6s ease"}}/>
                          </div>
                          {cat.nextDef&&<div style={{fontSize:9,color:`${C.silver}66`}}>{nl?`→ Lv ${cat.nextDef.n}: ${cat.nextDef.goal}`:`→ Lv ${cat.nextDef.n}: ${cat.nextDef.goalEN}`}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* 4 · Focus Statistics — cognitive training progress */}
          <FocusStats lang={lang} nl={nl}/>

          {/* 5 · Energy gauges — current physical/mental state */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[{label:t("dash.mental",lang),value:mental,color:C.purple},{label:t("dash.physical",lang),value:physical,color:C.teal},{label:t("dash.rest",lang),value:rest,color:"#5B6AD0"},{label:t("dash.balance",lang),value:balance,color:_T.accent}].map(g=>(
              <div key={g.label} style={{...s_GC({padding:14,marginBottom:0,display:"flex",alignItems:"center",justifyContent:"center"})}}>
                <ArcGauge value={g.value} label={g.label} color={g.color} size={100}/>
              </div>
            ))}
          </div>

          {/* 6 · Burnout risk — derived from energy */}
          <div style={s_GC({marginBottom:14})}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span style={{fontSize:13,color:C.white,fontWeight:600}}>{t("dash.burnout_risk",lang)}</span>
              <span style={{color:bc,fontSize:11,fontWeight:700,background:`${bc}15`,padding:"3px 10px",borderRadius:20}}>{burnout==="high"?t("burnout.high",lang):burnout==="medium"?t("burnout.medium",lang):t("burnout.low",lang)}</span>
            </div>
            <div style={{height:5,background:_T.bg4,borderRadius:3,overflow:"hidden",marginBottom:10}}>
              <div style={{width:`${burnoutPct}%`,height:"100%",background:`linear-gradient(90deg,${bc}88,${bc})`,borderRadius:3,transition:'width 0.6s ease'}}/>
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {avgSleepScore!==null&&<span style={{fontSize:10,color:avgSleepScore>=7?C.teal:avgSleepScore>=5?_T.accent:C.red}}>😴 {t("dash.sleep",lang)} {avgSleepScore}/10{sleepDaysLogged<7?` (${sleepDaysLogged}d)`:""}</span>}
              {moodScore!==null&&<span style={{fontSize:10,color:moodScore>=4?C.teal:moodScore>=3?_T.accent:C.red}}>💭 {t("dash.mood",lang)} {moodScore}/5</span>}
              {avgSleepScore===null&&<span style={{fontSize:10,color:`${C.silver}88`}}>{t("dash.sleep_none",lang)}</span>}
            </div>
          </div>

          {/* 7 · Sleep/Mood — wellness context */}
          <SleepMoodWeekCard lang={lang}/>

          {/* 8 · Social Stats — community at the bottom */}
          <SocialStatsCard userId={currentUserId} lang={lang} allUsers={allUsers||[]} friends={friends||[]} myPostCount={(myPosts||[]).length + (extraDbPosts||[]).length} />

        </div>
      )}

      {/* ── TIMELINE TAB ── */}
      {subTab==="timeline" && (()=>{
        const tlItems = buildTimeline();
        const grouped = {};
        tlItems.forEach(item => {
          const dk = new Date(item.date).toDateString();
          if (!grouped[dk]) grouped[dk] = [];
          grouped[dk].push(item);
        });

        return (
          <div className="fadeUp">
            {/* Hero banner (read-only) */}
            <div style={{position:"relative",border:`1.5px solid ${myAccentColor}33`,borderRadius:20,marginBottom:14}}>
              <div style={{height:120,borderRadius:"20px 20px 0 0",overflow:"hidden",position:"relative"}}>
                {bannerImg && <img src={bannerImg} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>}
                <div style={{position:"absolute",inset:0,background:`linear-gradient(135deg,${myBannerColor}${bannerImg?"cc":""},${myAccentColor}22)`}}/>
                <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at 75% 40%,${myAccentColor}28,transparent 65%)`}}/>
                {weekIntention&&<div style={{position:"absolute",bottom:10,left:72,right:16,fontSize:11,color:`${myAccentColor}cc`,fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>"{weekIntention}"</div>}
              </div>
              {/* Avatar — positioned relative to the outer wrapper so it isn't clipped by banner overflow:hidden */}
              <div style={{position:"absolute",bottom:-8,left:16,zIndex:5,border:`3px solid ${_T.bg3}`,borderRadius:"50%"}}>
                {avatarImg
                  ? <img src={avatarImg} alt="" style={{width:56,height:56,borderRadius:"50%",objectFit:"cover",display:"block"}}/>
                  : <div style={{width:56,height:56,borderRadius:"50%",background:`linear-gradient(135deg,${myAccentColor}44,${myAccentColor}22)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:700,color:myAccentColor}}>{getInitials(name||userName||"S")}</div>
                }
              </div>
              <div style={{background:_T.bg3,borderRadius:"0 0 20px 20px",padding:"16px 16px 14px",paddingLeft:86}}>
                <div style={{color:C.white,fontSize:15,fontWeight:600}}>{name||userName||"Sense Lifter"}</div>
                <div style={{fontSize:11,color:C.silver,marginTop:1}}>{tlItems.length} {nl?"activiteiten":"activities"}</div>
                <div style={{fontSize:9,color:C.silver,marginTop:3,fontFamily:"monospace",wordBreak:"break-all"}}>{currentUserId||localStorage.getItem('sl_user_id')||"no-id"}</div>
              </div>
            </div>

            {/* Share experience button */}
            <button onClick={()=>setShowShare(true)} style={{...s_PB({width:"100%",marginBottom:16}),display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:13}}>
              📸 {nl?"Ervaring delen":"Share experience"}
            </button>

            {/* Timeline feed */}
            {tlItems.length === 0 ? (
              <div style={{...s_GC({textAlign:"center",padding:36}),color:C.silver,fontSize:13}}>
                <div style={{fontSize:36,marginBottom:10}}>📖</div>
                {nl?"Jouw tijdlijn is leeg. Begin met oefeningen, games of deel een ervaring!":"Your timeline is empty. Start with exercises, games, or share an experience!"}
              </div>
            ) : (
              Object.entries(grouped).map(([dk, dayItems]) => (
                <div key={dk} style={{marginBottom:20}}>
                  {/* Day header */}
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <span style={{fontSize:11,color:C.silver,fontWeight:600,letterSpacing:1}}>{fmtDay(dayItems[0].date).toUpperCase()}</span>
                    <div style={{flex:1,height:1,background:_T.cardBorderBot}}/>
                  </div>

                  {dayItems.map((item, i) => {
                    // ── Post ──
                    if (item.type==="post") return (
                      <div key={i} style={s_GC({padding:"12px 14px",marginBottom:8})}>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                          <div style={{flexShrink:0}}>
                            {avatarImg
                              ? <img src={avatarImg} alt="" style={{width:32,height:32,borderRadius:"50%",objectFit:"cover"}}/>
                              : <div style={{width:32,height:32,borderRadius:"50%",background:`${myAccentColor}22`,border:`1.5px solid ${myAccentColor}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:myAccentColor}}>{getInitials(name||"S")}</div>
                            }
                          </div>
                          <div style={{flex:1}}>
                            <div style={{color:C.white,fontSize:12,fontWeight:600}}>{name||userName}</div>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontSize:9,color:C.silver}}>{timeAgo(item.date,lang)}</span>
                              <span style={{fontSize:9,background:item.public?`${C.teal}20`:`${C.silver}15`,color:item.public?C.teal:C.silver,borderRadius:6,padding:"1px 6px"}}>{item.public?(nl?"Publiek":"Public"):(nl?"Privé":"Private")}</span>
                            </div>
                          </div>
                          <button onClick={()=>setDeleteConfirm(item)} style={{background:"none",border:"none",color:`${C.silver}66`,fontSize:14,cursor:"pointer",padding:4}}>🗑</button>
                        </div>
                        {item.img&&<img src={item.img} alt="" onClick={()=>setLightboxSrc(item.img)} style={{width:"100%",maxHeight:220,objectFit:"cover",borderRadius:10,marginBottom:8,border:`1px solid ${_T.cardBorderBot}`,cursor:"pointer"}}/>}
                        {item.text&&<div style={{color:C.silver2,fontSize:13,lineHeight:1.6,marginBottom:item.feeling?6:0}}>{item.text}</div>}
                        {item.feeling && (
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8,paddingTop:8,borderTop:`1px solid ${_T.cardBorderBot}`}}>
                            <div style={{display:"inline-flex",alignItems:"center",gap:5,background:`${_T.accent}15`,border:`1px solid ${_T.accent}33`,borderRadius:20,padding:"4px 10px",fontSize:12}}>
                              <span>{item.feeling.emoji}</span>
                              <span style={{color:_T.accent,fontWeight:600}}>{nl?item.feeling.labelNL:item.feeling.label}</span>
                              {item.resonanceCount > 0 && <span style={{fontSize:10,color:_T.accent,marginLeft:2}}>⌀{item.resonanceAvg}% · {item.resonanceCount}</span>}
                            </div>
                            <button onClick={()=>openResonanceModal(item)} style={{background:`${_T.accent}15`,border:`1px solid ${_T.accent}33`,color:_T.accent,borderRadius:20,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                              💙 {nl?"Resonantie":"Resonance"}
                            </button>
                          </div>
                        )}
                      </div>
                    );

                    // ── Exercise ──
                    if (item.type==="exercise") {
                      const ex = EXERCISES.find(e=>e.id===item.exId);
                      if (!ex) return null;
                      const cats = item.cats.map(cid=>GROWTH_CATEGORIES.find(c=>c.id===cid)).filter(Boolean);
                      return (
                        <div key={i} style={s_GC({padding:"10px 14px",marginBottom:8})}>
                          <div style={{display:"flex",gap:10,alignItems:"center"}}>
                            <div style={{width:40,height:40,borderRadius:12,background:`${ex.color}18`,border:`1px solid ${ex.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{ex.icon}</div>
                            <div style={{flex:1}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?ex.label:ex.labelEN}</div>
                                <span style={{fontSize:10,color:C.silver}}>{timeAgo(item.date,lang)}</span>
                              </div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>
                                {cats.map(cat=>(
                                  <span key={cat.id} style={{fontSize:9,background:`${cat.color}15`,border:`1px solid ${cat.color}33`,color:cat.color,borderRadius:8,padding:"2px 6px"}}>{cat.icon} {nl?cat.label:cat.labelEN}</span>
                                ))}
                              </div>
                              {item.feelings?.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>{item.feelings.map((f,fi)=><span key={fi} style={{fontSize:9,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:6,padding:"2px 6px"}}>{f}</span>)}</div>}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // ── Level up ──
                    if (item.type==="levelup") {
                      const cat = GROWTH_CATEGORIES.find(c=>c.id===item.catId);
                      if (!cat) return null;
                      return (
                        <div key={i} style={{...s_GC({padding:"10px 14px",marginBottom:8}),border:`1.5px solid ${cat.color}44`,background:`${cat.color}08`}}>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <div style={{width:40,height:40,borderRadius:12,background:`${cat.color}20`,border:`1px solid ${cat.color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>⬆️</div>
                            <div style={{flex:1}}>
                              <div style={{color:cat.color,fontSize:13,fontWeight:700}}>{nl?"Level up!":"Level up!"} {cat.icon} {nl?cat.label:cat.labelEN}</div>
                              <div style={{color:C.silver,fontSize:11,marginTop:2}}>{nl?`Niveau ${item.level} bereikt`:`Reached level ${item.level}`}</div>
                            </div>
                            <div style={{background:`${cat.color}20`,borderRadius:10,padding:"4px 10px"}}>
                              <span style={{color:cat.color,fontSize:14,fontWeight:700}}>Lv {item.level}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // ── Game ──
                    if (item.type==="game") {
                      const gm = GAME_META[item.gameId];
                      if (!gm) return null;
                      return (
                        <div key={i} style={s_GC({padding:"10px 14px",marginBottom:8})}>
                          <div style={{display:"flex",gap:10,alignItems:"center"}}>
                            <div style={{width:40,height:40,borderRadius:12,background:`${gm.color}18`,border:`1px solid ${gm.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{gm.icon}</div>
                            <div style={{flex:1}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?gm.labelNL:gm.label}</div>
                                <span style={{fontSize:10,color:C.silver}}>{timeAgo(item.date,lang)}</span>
                              </div>
                              <div style={{color:C.silver,fontSize:11,marginTop:2}}>
                                {item.count}× {nl?"gespeeld":"played"} · {nl?"beste":"best"} <span style={{color:gm.color,fontWeight:600}}>{item.best} pts</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              ))
            )}
          </div>
        );
      })()}

      {/* ── PROFILE TAB ── */}
      {/* ── CONTACTS TAB ── */}
      {subTab==="contacts" && (
        <div className="fadeUp">
          <div style={s_GC({marginBottom:14})}>
            <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>👥 {nl?"CONTACTEN IMPORTEREN":"IMPORT CONTACTS"}</div>

            {/* Why add contacts — benefit reasons carousel */}
            {(() => {
              const REASONS = [
                {
                  icon:"🇪🇺",
                  title: nl?"Jouw data op een Europese server":"Your data on a European server",
                  body:  nl
                    ? "Kopieer je contacten van Meta of Google — naar een veilige Nederlandse server. Jouw data, onder Europese wet."
                    : "Copy your contacts from Meta or Google — to a secure Dutch server. Your data, under European law.",
                },
                {
                  icon:"💾",
                  title: nl?"Een back-up van je contacten":"A backup of your contacts",
                  body:  nl
                    ? "Verlies nooit meer een contactpersoon. Je nummers en e-mails veilig opgeslagen, onafhankelijk van je telefoon."
                    : "Never lose a contact again. Numbers and emails stored safely, independent of your phone.",
                },
                {
                  icon:"🤝",
                  title: nl?"Eén platform voor alles":"One platform for everything",
                  body:  nl
                    ? "Bel direct, stuur een WhatsApp-uitnodiging en ontdek gezondheidsvoordelen — alles in één app."
                    : "Call directly, send a WhatsApp invite and discover health benefits — all in one app.",
                },
              ];
              const r = REASONS[carouselIdx];
              return (
                <div
                  style={{marginBottom:14,userSelect:"none"}}
                  onTouchStart={e=>{ carouselTouch.current = e.touches[0].clientX; }}
                  onTouchEnd={e=>{
                    if (carouselTouch.current === null) return;
                    const dx = e.changedTouches[0].clientX - carouselTouch.current;
                    if (Math.abs(dx) > 40) setCarouselIdx(i => (i + (dx < 0 ? 1 : -1) + 3) % 3);
                    carouselTouch.current = null;
                  }}
                >
                  <div style={{display:"flex",gap:12,background:_T.bg4,borderRadius:12,padding:"12px 14px",minHeight:80,transition:"opacity 0.25s"}}>
                    <div style={{fontSize:22,flexShrink:0,lineHeight:1.2,marginTop:1}}>{r.icon}</div>
                    <div>
                      <div style={{color:C.white,fontSize:12,fontWeight:700,marginBottom:3}}>{r.title}</div>
                      <div style={{color:C.silver,fontSize:11,lineHeight:1.6}}>{r.body}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",justifyContent:"center",gap:6,marginTop:8}}>
                    {[0,1,2].map(i=>(
                      <div key={i} onClick={()=>setCarouselIdx(i)} style={{
                        width:i===carouselIdx?16:5,height:5,borderRadius:3,
                        background:i===carouselIdx?_T.accent:'rgba(255,255,255,0.18)',
                        cursor:"pointer",transition:"width 0.3s,background 0.3s",
                      }}/>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button onClick={importPhoneContacts} style={{display:"flex",alignItems:"center",gap:10,background:`${_T.accent}18`,border:`1px solid ${_T.accent}55`,borderRadius:12,padding:"12px 14px",cursor:"pointer",textAlign:"left"}}>
                <span style={{fontSize:20}}>📱</span>
                <div>
                  <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?"Importeer van telefoon":"Import from phone"}</div>
                  <div style={{color:C.silver,fontSize:11,marginTop:2}}>{nl?"Gebruik de contacten app van je telefoon":"Use your phone's contacts app"}</div>
                </div>
              </button>
              <button onClick={()=>{
                const inviterName = (() => { try { return JSON.parse(localStorage.getItem('sl_name')||'""'); } catch { return ''; } })();
                const refParam = currentUserId ? `?ref=${encodeURIComponent(currentUserId)}` : '';
                const msg = nl
                  ? `Hé! 👋 ${inviterName} gebruikt Sense Lifting voor mentale & fysieke training. Doe mee: https://senselifting.com${refParam}`
                  : `Hey! 👋 ${inviterName} uses Sense Lifting for mental & physical training. Join: https://senselifting.com${refParam}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,'_blank');
              }} style={{display:"flex",alignItems:"center",gap:10,background:"rgba(37,211,102,0.12)",border:"1px solid rgba(37,211,102,0.4)",borderRadius:12,padding:"12px 14px",cursor:"pointer",textAlign:"left"}}>
                <span style={{fontSize:20}}>💬</span>
                <div>
                  <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?"Deel via WhatsApp":"Share via WhatsApp"}</div>
                  <div style={{color:C.silver,fontSize:11,marginTop:2}}>{nl?"Stuur een uitnodiging via WhatsApp":"Send a general invite via WhatsApp"}</div>
                </div>
              </button>
            </div>
          </div>

          <div style={s_GC()}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600}}>📋 {nl?"MIJN CONTACTEN":"MY CONTACTS"}</div>
              <button onClick={loadContacts} style={{background:"transparent",border:"none",color:C.silver,fontSize:12,cursor:"pointer"}}>
                {contactsLoading?'···':(nl?"↻ Verversen":"↻ Refresh")}
              </button>
            </div>
            <input
              type="text"
              value={contactSearch}
              onChange={e=>setContactSearch(e.target.value)}
              placeholder={nl?"Zoek contacten...":"Search contacts..."}
              style={{
                width:"100%",boxSizing:"border-box",
                background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,
                borderRadius:10,padding:"8px 12px",color:C.white,fontSize:12,
                marginBottom:10,outline:"none",fontFamily:"Inter,system-ui,sans-serif",
              }}
            />

            {!contactsLoaded && !contactsLoading && (
              <div style={{color:C.silver,fontSize:12,textAlign:"center",padding:"12px 0"}}>
                {nl?"Importeer contacten om ze hier te zien.":"Import contacts to see them here."}
              </div>
            )}
            {contactsLoading && <div style={{color:C.silver,fontSize:12,textAlign:"center",padding:16}}>···</div>}

            {contactsLoaded && contacts.length === 0 && (
              <div style={{color:C.silver,fontSize:12,textAlign:"center",padding:"12px 0"}}>
                {nl?"Nog geen contacten geïmporteerd.":"No contacts imported yet."}
              </div>
            )}

            {contacts.filter(c => !contactSearch || (c.name||'').toLowerCase().includes(contactSearch.toLowerCase()) || (c.email||'').toLowerCase().includes(contactSearch.toLowerCase())).map(c => (
              <div key={c.id} onClick={()=>{setSelectedContact(c);setEditingContact(null);}} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${_T.cardBorderBot}`,cursor:"pointer"}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:`${_T.accent}22`,border:`1px solid ${_T.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:_T.accent,flexShrink:0}}>
                  {(c.name||'?').slice(0,2).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:C.white,fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
                    {c.sl_user_id && <span style={{fontSize:10,color:"#4AC87A",background:"rgba(74,200,122,0.12)",borderRadius:10,padding:"1px 7px",fontWeight:600}}>✓ SL</span>}
                    {c.invite_sent && !c.sl_user_id && <span style={{fontSize:10,color:"#C8A84B",background:"rgba(200,168,75,0.1)",borderRadius:10,padding:"1px 7px"}}>📧 {nl?"Uitgenodigd":"Invited"}</span>}
                    {c.sms_sent && !c.sl_user_id && <span style={{fontSize:10,color:"#7B6FD0",background:"rgba(123,111,208,0.12)",borderRadius:10,padding:"1px 7px"}}>💬 SMS</span>}
                    {c.email && <span style={{fontSize:10,color:C.silver,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:120}}>{c.email}</span>}
                  </div>
                </div>
                <span style={{color:C.silver,fontSize:16}}>›</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CONTACT DETAIL SHEET ── */}
      {selectedContact && ReactDOM.createPortal(
        <div onClick={()=>{if(!editingContact){setSelectedContact(null);}}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"flex-end",backdropFilter:"blur(4px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:_T.bg2,borderRadius:"24px 24px 0 0",paddingBottom:"env(safe-area-inset-bottom,20px)",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 -8px 40px rgba(0,0,0,0.5)"}}>
            {/* Handle bar */}
            <div style={{display:"flex",justifyContent:"center",paddingTop:12,paddingBottom:4}}>
              <div style={{width:44,height:4,borderRadius:2,background:_T.cardBorderBot}}/>
            </div>

            {/* ── Hero header ── */}
            <div style={{position:"relative",padding:"16px 20px 20px",borderBottom:`1px solid ${_T.cardBorderBot}`}}>
              {/* Close button */}
              <button onClick={()=>{setSelectedContact(null);setEditingContact(null);}} style={{position:"absolute",top:14,right:16,width:30,height:30,borderRadius:"50%",background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>✕</button>

              <div style={{display:"flex",alignItems:"center",gap:16}}>
                {/* Avatar */}
                <div style={{flexShrink:0,width:60,height:60,borderRadius:18,background:`linear-gradient(135deg,${_T.grad1}33,${_T.grad2}33)`,border:`2px solid ${_T.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:800,color:_T.accent,letterSpacing:-1}}>
                  {(selectedContact.name||'?').slice(0,2).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  {editingContact
                    ? <input value={editingContact.name} onChange={e=>setEditingContact(p=>({...p,name:e.target.value}))} style={{background:_T.bg4,border:`1px solid ${_T.accent}66`,borderRadius:10,padding:"8px 12px",color:C.white,fontSize:17,fontWeight:700,width:"100%",boxSizing:"border-box"}}/>
                    : <div style={{color:C.white,fontSize:18,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedContact.name}</div>
                  }
                  {/* Status badges */}
                  <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:6}}>
                    {selectedContact.sl_user_id
                      ? <span style={{fontSize:10,color:"#4AC87A",background:"rgba(74,200,122,0.14)",border:"1px solid rgba(74,200,122,0.3)",borderRadius:20,padding:"2px 9px",fontWeight:700}}>✓ {nl?"Sense Lifting lid":"Sense Lifting member"}</span>
                      : <span style={{fontSize:10,color:C.silver,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:20,padding:"2px 9px"}}>{nl?"Geen SL account":"No SL account"}</span>
                    }
                    {selectedContact.invite_sent && !selectedContact.sl_user_id && <span style={{fontSize:10,color:"#C8A84B",background:"rgba(200,168,75,0.12)",border:"1px solid rgba(200,168,75,0.3)",borderRadius:20,padding:"2px 9px"}}>📧 {nl?"E-mail verstuurd":"Email sent"}</span>}
                    {selectedContact.sms_sent   && !selectedContact.sl_user_id && <span style={{fontSize:10,color:"#7B6FD0",background:"rgba(123,111,208,0.12)",border:"1px solid rgba(123,111,208,0.3)",borderRadius:20,padding:"2px 9px"}}>💬 SMS {nl?"verstuurd":"sent"}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Contact info ── */}
            <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:10}}>

              {/* Phone numbers */}
              {(selectedContact.phone || editingContact) && (
                <div style={{background:_T.bg4,borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"10px 14px 6px",fontSize:10,color:C.silver,letterSpacing:2,fontWeight:700}}>{nl?"TELEFOON":"PHONE"}</div>
                  {editingContact
                    ? <div style={{padding:"0 14px 12px"}}>
                        <input value={editingContact.phone} onChange={e=>setEditingContact(p=>({...p,phone:e.target.value}))} placeholder={nl?"Nummers (komma-gescheiden)":"Numbers (comma-separated)"} style={{background:_T.bg2,border:`1px solid ${_T.accent}44`,borderRadius:10,padding:"9px 12px",color:C.white,fontSize:13,width:"100%",boxSizing:"border-box"}}/>
                      </div>
                    : selectedContact.phone.split(',').map(p=>p.trim()).filter(Boolean).map((p,i,arr) => (
                        <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px",borderTop: i>0?`1px solid ${_T.bg2}`:undefined}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:16}}>📱</span>
                            <span style={{color:C.white,fontSize:14,fontWeight:500}}>{p}</span>
                          </div>
                          <div style={{display:"flex",gap:6}}>
                            <a href={`tel:${p.replace(/\s/g,'')}`} style={{background:`${_T.accent}18`,border:`1px solid ${_T.accent}44`,borderRadius:8,padding:"5px 11px",color:_T.accent,fontSize:11,fontWeight:700,textDecoration:"none"}}>📞 {nl?"Bel":"Call"}</a>
                            <a href={`https://wa.me/${p.replace(/[^\d+]/g,'').replace(/^0031/,'+31').replace(/^00/,'+').replace(/^0(\d)/,'+31$1')}`} target="_blank" rel="noreferrer" style={{background:"rgba(37,211,102,0.12)",border:"1px solid rgba(37,211,102,0.35)",borderRadius:8,padding:"5px 11px",color:"#25D366",fontSize:11,fontWeight:700,textDecoration:"none"}}>WA</a>
                          </div>
                        </div>
                      ))
                  }
                </div>
              )}

              {/* Email */}
              {(selectedContact.email || editingContact) && (
                <div style={{background:_T.bg4,borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"10px 14px 6px",fontSize:10,color:C.silver,letterSpacing:2,fontWeight:700}}>E-MAIL</div>
                  {editingContact
                    ? <div style={{padding:"0 14px 12px"}}>
                        <input value={editingContact.email} onChange={e=>setEditingContact(p=>({...p,email:e.target.value}))} type="email" placeholder="Email" style={{background:_T.bg2,border:`1px solid ${_T.accent}44`,borderRadius:10,padding:"9px 12px",color:C.white,fontSize:13,width:"100%",boxSizing:"border-box"}}/>
                      </div>
                    : <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                          <span style={{fontSize:16}}>✉️</span>
                          <span style={{color:C.white,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedContact.email}</span>
                        </div>
                        <a href={`mailto:${selectedContact.email}`} style={{flexShrink:0,background:`${_T.accent}18`,border:`1px solid ${_T.accent}44`,borderRadius:8,padding:"5px 11px",color:_T.accent,fontSize:11,fontWeight:700,textDecoration:"none",marginLeft:8}}>{nl?"Mail →":"Mail →"}</a>
                      </div>
                  }
                </div>
              )}

              {/* No contact info */}
              {!selectedContact.phone && !selectedContact.email && !editingContact && (
                <div style={{textAlign:"center",padding:"16px 0",color:C.silver,fontSize:12}}>
                  {nl?"Geen contactgegevens opgeslagen.":"No contact details saved."}
                  <div style={{marginTop:8,fontSize:11,color:`${C.silver}88`}}>{nl?"Tik op Bewerken om details toe te voegen.":"Tap Edit to add details."}</div>
                </div>
              )}

              {/* Invite status info */}
              {!selectedContact.sl_user_id && !editingContact && (
                <div style={{background:`${_T.accent}0A`,border:`1px solid ${_T.accent}22`,borderRadius:14,padding:"12px 14px"}}>
                  <div style={{fontSize:10,color:_T.accent,letterSpacing:2,fontWeight:700,marginBottom:6}}>{nl?"UITNODIGINGSSTATUS":"INVITE STATUS"}</div>
                  <div style={{fontSize:12,color:C.silver,lineHeight:1.6}}>
                    {selectedContact.invite_token
                      ? nl ? "✓ Persoonlijke uitnodigingslink aangemaakt." : "✓ Personal invite link created."
                      : nl ? "Nog geen uitnodigingslink — wordt aangemaakt bij WhatsApp uitnodigen." : "No invite link yet — created when you invite via WhatsApp."
                    }
                  </div>
                  {(selectedContact.invite_sent || selectedContact.sms_sent) && (
                    <div style={{marginTop:6,display:"flex",gap:8,flexWrap:"wrap"}}>
                      {selectedContact.invite_sent && <span style={{fontSize:10,color:"#C8A84B"}}>📧 {nl?"E-mail verstuurd":"Email sent"}</span>}
                      {selectedContact.sms_sent   && <span style={{fontSize:10,color:"#7B6FD0"}}>💬 {nl?"SMS verstuurd":"SMS sent"}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Action buttons ── */}
            <div style={{padding:"0 20px 16px"}}>
              {!editingContact ? (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {/* Primary: WhatsApp invite */}
                  {!selectedContact.sl_user_id && (
                    <button onClick={()=>waInvite(selectedContact)} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"rgba(37,211,102,0.14)",border:"1px solid rgba(37,211,102,0.4)",borderRadius:14,padding:"13px 0",color:"#25D366",fontSize:14,fontWeight:700,cursor:"pointer",width:"100%"}}>
                      <span style={{fontSize:17}}>💬</span> {nl?"Uitnodigen via WhatsApp":"Invite via WhatsApp"}
                    </button>
                  )}
                  {/* Secondary row */}
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>setEditingContact({name:selectedContact.name,email:selectedContact.email||'',phone:selectedContact.phone||''})} style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:12,padding:"11px 0",color:C.silver,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                      ✏️ {nl?"Bewerken":"Edit"}
                    </button>
                    <button onClick={()=>{ if(window.confirm(nl?`${selectedContact.name} verwijderen?`:`Delete ${selectedContact.name}?`)) deleteContact(selectedContact.id); }} style={{background:`${C.red}10`,border:`1px solid ${C.red}30`,borderRadius:12,padding:"11px 16px",color:C.red,fontSize:13,cursor:"pointer"}}>
                      🗑
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{display:"flex",gap:8}}>
                  <button onClick={saveContact} disabled={contactSaving} style={{flex:1,background:`linear-gradient(135deg,${_T.grad1},${_T.grad2})`,border:"none",borderRadius:14,padding:"13px 0",color:"#0a1220",fontSize:14,fontWeight:800,cursor:contactSaving?"default":"pointer",opacity:contactSaving?0.7:1}}>
                    {contactSaving?(nl?"Opslaan...":"Saving..."):(nl?"✓ Opslaan":"✓ Save")}
                  </button>
                  <button onClick={()=>setEditingContact(null)} style={{flex:1,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:14,padding:"13px 0",color:C.silver,fontSize:14,cursor:"pointer"}}>
                    {nl?"Annuleren":"Cancel"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {subTab==="profile" && (
        <div className="fadeUp">
          {/* 1 · Profile appearance — hero banner & avatar editor */}
          <HeroBannerEditor name={name||userName||"Sense Lifter"} bannerColor={myBannerColor} setBannerColor={setMyBannerColor} accentColor={myAccentColor} setAccentColor={setMyAccentColor} intention={weekIntention} lang={lang} edit={edit} setEdit={setEdit} tmp={tmp} setTmp={setTmp} setName={setName} userId={currentUserId}/>

          {/* 2 · Badges & achievements */}
          <ProfileBadges userId={currentUserId} lang={lang} isOwnProfile={true} />

          {/* 3 · Personal Session Files (coach history) */}
          {(()=>{
            let sessions=[];
            try{sessions=JSON.parse(localStorage.getItem('sl_coach_sessions')||'[]');}catch{}
            return(
              <div style={s_GC({marginBottom:14})}>
                <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>🗂 {nl?"PERSOONLIJKE SESSIEDOSSIERS":"PERSONAL SESSION FILES"}</div>
                {sessions.length===0?(
                  <div style={{color:C.silver,fontSize:12,textAlign:"center",padding:"12px 0"}}>{nl?"Nog geen sessies opgeslagen. Ga naar de Sense Lifting Coach om een sessie te starten.":"No sessions saved yet. Go to Sense Lifting Coach to start a session."}</div>
                ):(
                  sessions.map(s=>{
                    const isOpen=expandedSessionId===s.id;
                    return(
                      <div key={s.id} style={{marginBottom:8,background:`${s.color||_T.accent}0D`,border:`1px solid ${s.color||_T.accent}33`,borderRadius:12,overflow:"hidden"}}>
                        <div onClick={()=>setExpandedSessionId(isOpen?null:s.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",cursor:"pointer"}}>
                          <div style={{width:32,height:32,borderRadius:8,background:`${s.color||_T.accent}22`,border:`1px solid ${s.color||_T.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{s.icon||"🦋"}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{color:"#EEF3FF",fontSize:12,fontWeight:600}}>{nl?s.labelNL:s.labelEN}</div>
                            <div style={{color:C.silver,fontSize:10,marginTop:1}}>{s.date}</div>
                          </div>
                          <span style={{color:C.silver,fontSize:14}}>{isOpen?"▲":"▼"}</span>
                        </div>
                        {isOpen&&(
                          <div style={{padding:"0 12px 12px",borderTop:`1px solid ${s.color||_T.accent}22`}}>
                            <div style={{color:C.silver2,fontSize:11,lineHeight:1.6,marginTop:10,marginBottom:(s.actions||[]).length?10:0}}>{s.summary}</div>
                            {(s.actions||[]).length>0&&(
                              <div>
                                <div style={{fontSize:10,color:C.silver,letterSpacing:1,marginBottom:6}}>{nl?"GEPLANDE ACTIES:":"PLANNED ACTIONS:"}</div>
                                {s.actions.map((a,i)=>(
                                  <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:11,marginBottom:4}}>
                                    <span style={{fontSize:13}}>{a.icon||"📌"}</span>
                                    <span style={{color:(s.scheduled||[]).includes(a.label)?"#22c55e":C.silver2}}>{a.label}</span>
                                    {(s.scheduled||[]).includes(a.label)&&<span style={{fontSize:10,color:"#22c55e"}}>✓ {nl?"ingepland":"scheduled"}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            );
          })()}

          {/* 3b · Snelle Reset saved sessions */}
          {(()=>{
            let resets=[];
            try{resets=JSON.parse(localStorage.getItem('sl_saved_sessions')||'[]');}catch{}
            if(resets.length===0) return null;
            return(
              <div style={s_GC({marginBottom:14})}>
                <div style={{fontSize:13,color:'#7B6FD0',letterSpacing:3,fontWeight:600,marginBottom:12}}>🔄 {nl?"GROUNDING SESSIES":"RESET SESSIONS"}</div>
                {resets.map(s=>{
                  const isOpen=expandedSessionId===`reset_${s.id}`;
                  const g=s.grounding||{};
                  return(
                    <div key={s.id} style={{marginBottom:8,background:'#7B6FD010',border:'1px solid #7B6FD033',borderRadius:12,overflow:'hidden'}}>
                      <div onClick={()=>setExpandedSessionId(isOpen?null:`reset_${s.id}`)} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',cursor:'pointer'}}>
                        <div style={{width:32,height:32,borderRadius:8,background:'#7B6FD022',border:'1px solid #7B6FD044',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,flexShrink:0}}>🔄</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{color:'#EEF3FF',fontSize:12,fontWeight:600}}>{nl?'Snelle Reset':'Quick Reset'}</div>
                          <div style={{color:C.silver,fontSize:10,marginTop:1}}>{s.date}</div>
                        </div>
                        <span style={{color:C.silver,fontSize:14}}>{isOpen?'▲':'▼'}</span>
                      </div>
                      {isOpen&&(
                        <div style={{padding:'0 12px 12px',borderTop:'1px solid #7B6FD022'}}>
                          {g.sees&&g.sees.length>0&&(<div style={{marginTop:10}}><div style={{color:'#7B6FD0',fontSize:10,fontWeight:700,letterSpacing:1,marginBottom:4}}>👁 {nl?'GEZIEN':'SAW'}</div>{g.sees.map((v,i)=><div key={i} style={{color:C.silver2,fontSize:11,lineHeight:1.6}}>{v}</div>)}</div>)}
                          {g.feels&&g.feels.length>0&&(<div style={{marginTop:8}}><div style={{color:'#7B6FD0',fontSize:10,fontWeight:700,letterSpacing:1,marginBottom:4}}>🤚 {nl?'GEVOELD':'FELT'}</div>{g.feels.map((v,i)=><div key={i} style={{color:C.silver2,fontSize:11,lineHeight:1.6}}>{v}</div>)}</div>)}
                          {g.hears&&g.hears.length>0&&(<div style={{marginTop:8}}><div style={{color:'#7B6FD0',fontSize:10,fontWeight:700,letterSpacing:1,marginBottom:4}}>👂 {nl?'GEHOORD':'HEARD'}</div>{g.hears.map((v,i)=><div key={i} style={{color:C.silver2,fontSize:11,lineHeight:1.6}}>{v}</div>)}</div>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* 3c · Account settings — email/password/phone (email-auth users only) */}
          {isEmailUser && (
            <div style={s_GC({marginBottom:14})}>
              <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>🔐 {nl?"ACCOUNT INSTELLINGEN":"ACCOUNT SETTINGS"}</div>

              {acctMsg && (
                <div style={{padding:"8px 12px",borderRadius:10,marginBottom:12,fontSize:12,background:acctMsg.type==='ok'?'rgba(74,200,122,0.12)':'rgba(192,80,80,0.12)',border:`1px solid ${acctMsg.type==='ok'?'rgba(74,200,122,0.4)':'rgba(192,80,80,0.4)'}`,color:acctMsg.type==='ok'?'#4AC87A':C.red}}>
                  {acctMsg.type==='ok'?'✓ ':'⚠ '}{acctMsg.text}
                </div>
              )}

              {/* Change email */}
              <div style={{borderBottom:`1px solid ${_T.cardBorderBot}`,paddingBottom:12,marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:chgEmailOpen?10:0}}>
                  <span style={{color:C.white,fontSize:13}}>✉️ {nl?"E-mailadres wijzigen":"Change email"}</span>
                  <button onClick={()=>{setChgEmailOpen(v=>!v);setAcctMsg(null);}} style={{background:`${_T.accent}15`,border:`1px solid ${_T.accent}55`,color:_T.accent,borderRadius:8,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                    {chgEmailOpen?(nl?"Annuleer":"Cancel"):(nl?"Wijzig":"Change")}
                  </button>
                </div>
                {chgEmailOpen&&(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <input type="email" placeholder={nl?"Nieuw e-mailadres":"New email address"} value={chgNewEmail} onChange={e=>setChgNewEmail(e.target.value)}
                      style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:10,padding:"9px 12px",color:C.white,fontSize:13,outline:"none",width:"100%",boxSizing:"border-box"}}/>
                    <input type="password" placeholder={nl?"Huidig wachtwoord":"Current password"} value={chgEmailPwd} onChange={e=>setChgEmailPwd(e.target.value)}
                      style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:10,padding:"9px 12px",color:C.white,fontSize:13,outline:"none",width:"100%",boxSizing:"border-box"}}/>
                    <button disabled={acctLoading} onClick={async()=>{
                      setAcctLoading(true);setAcctMsg(null);
                      const res=await fetch(`${BASE}/auth/change-email`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({uid:currentUserId,password:chgEmailPwd,newEmail:chgNewEmail.trim().toLowerCase()})});
                      const d=await res.json();
                      setAcctLoading(false);
                      if(res.ok){setAcctMsg({type:'ok',text:nl?'E-mailadres bijgewerkt.':'Email updated.'});setChgEmailOpen(false);setChgEmailPwd('');setChgNewEmail('');}
                      else setAcctMsg({type:'err',text:d.error==='incorrect password'?(nl?'Onjuist wachtwoord.':'Incorrect password.'):(d.error==='email already in use'?(nl?'E-mail al in gebruik.':'Email already in use.'):(nl?'Fout. Probeer opnieuw.':'Error. Try again.'))});
                    }} style={{background:`${_T.accent}22`,border:`1px solid ${_T.accent}`,color:_T.accent,borderRadius:10,padding:"9px",cursor:"pointer",fontSize:13,fontWeight:600,opacity:acctLoading?0.6:1}}>
                      {acctLoading?(nl?'Bezig...':'Saving...'):(nl?'Opslaan →':'Save →')}
                    </button>
                  </div>
                )}
              </div>

              {/* Change password */}
              <div style={{borderBottom:`1px solid ${_T.cardBorderBot}`,paddingBottom:12,marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:chgPwdOpen?10:0}}>
                  <span style={{color:C.white,fontSize:13}}>🔑 {nl?"Wachtwoord wijzigen":"Change password"}</span>
                  <button onClick={()=>{setChgPwdOpen(v=>!v);setAcctMsg(null);}} style={{background:`${_T.accent}15`,border:`1px solid ${_T.accent}55`,color:_T.accent,borderRadius:8,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                    {chgPwdOpen?(nl?"Annuleer":"Cancel"):(nl?"Wijzig":"Change")}
                  </button>
                </div>
                {chgPwdOpen&&(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <input type="password" placeholder={nl?"Huidig wachtwoord":"Current password"} value={chgOldPwd} onChange={e=>setChgOldPwd(e.target.value)}
                      style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:10,padding:"9px 12px",color:C.white,fontSize:13,outline:"none",width:"100%",boxSizing:"border-box"}}/>
                    <input type="password" placeholder={nl?"Nieuw wachtwoord (min. 6)":"New password (min. 6)"} value={chgNewPwd} onChange={e=>setChgNewPwd(e.target.value)}
                      style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:10,padding:"9px 12px",color:C.white,fontSize:13,outline:"none",width:"100%",boxSizing:"border-box"}}/>
                    <button disabled={acctLoading} onClick={async()=>{
                      setAcctLoading(true);setAcctMsg(null);
                      const res=await fetch(`${BASE}/auth/change-password`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({uid:currentUserId,oldPassword:chgOldPwd,newPassword:chgNewPwd})});
                      const d=await res.json();
                      setAcctLoading(false);
                      if(res.ok){setAcctMsg({type:'ok',text:nl?'Wachtwoord bijgewerkt.':'Password updated.'});setChgPwdOpen(false);setChgOldPwd('');setChgNewPwd('');}
                      else setAcctMsg({type:'err',text:d.error==='incorrect current password'?(nl?'Onjuist huidig wachtwoord.':'Incorrect current password.'):(nl?'Fout. Probeer opnieuw.':'Error. Try again.')});
                    }} style={{background:`${_T.accent}22`,border:`1px solid ${_T.accent}`,color:_T.accent,borderRadius:10,padding:"9px",cursor:"pointer",fontSize:13,fontWeight:600,opacity:acctLoading?0.6:1}}>
                      {acctLoading?(nl?'Bezig...':'Saving...'):(nl?'Opslaan →':'Save →')}
                    </button>
                  </div>
                )}
              </div>

              {/* Phone number */}
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:phoneEdit?10:0}}>
                  <div>
                    <div style={{color:C.white,fontSize:13}}>📱 {nl?"Telefoonnummer":"Phone number"}</div>
                    {phoneNum&&!phoneEdit&&<div style={{color:C.silver,fontSize:11,marginTop:2}}>{phoneNum}</div>}
                  </div>
                  <button onClick={()=>setPhoneEdit(v=>!v)} style={{background:`${_T.accent}15`,border:`1px solid ${_T.accent}55`,color:_T.accent,borderRadius:8,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                    {phoneEdit?(nl?"Annuleer":"Cancel"):(phoneNum?(nl?"Wijzig":"Change"):(nl?"Toevoegen":"Add"))}
                  </button>
                </div>
                {phoneEdit&&(
                  <div style={{display:"flex",gap:8}}>
                    <input type="tel" placeholder="+31 6 12345678" value={phoneNum} onChange={e=>setPhoneNum(e.target.value)}
                      style={{flex:1,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:10,padding:"9px 12px",color:C.white,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                    <button disabled={phoneLoading} onClick={async()=>{
                      setPhoneLoading(true);
                      await fetch(`${BASE}/profile/phone`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({uid:currentUserId,phone:phoneNum})});
                      setPhoneLoading(false);setPhoneEdit(false);setAcctMsg({type:'ok',text:nl?'Telefoonnummer opgeslagen.':'Phone saved.'});
                    }} style={{background:`${_T.accent}22`,border:`1px solid ${_T.accent}`,color:_T.accent,borderRadius:10,padding:"9px 14px",cursor:"pointer",fontSize:13,fontWeight:600,flexShrink:0,opacity:phoneLoading?0.6:1}}>
                      {phoneLoading?'...':'✓'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 4 · Visual theme */}
          {themeUnlockPopup && ReactDOM.createPortal(
            <div style={{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",zIndex:9999,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
              onClick={()=>setThemeUnlockPopup(null)}>
              <div style={{background:_T.bg2,border:`1px solid ${getTheme(themeUnlockPopup.id).accent}`,borderRadius:24,padding:"28px 24px",width:"100%",maxWidth:320,textAlign:"center",boxShadow:`0 0 40px ${getTheme(themeUnlockPopup.id).accent}44`}} onClick={e=>e.stopPropagation()}>
                <div style={{fontSize:44,marginBottom:10}}>{themeUnlockPopup.icon}</div>
                <div style={{color:C.white,fontSize:18,fontWeight:700,marginBottom:6}}>{nl?"🎉 Thema ontgrendeld!":"🎉 Theme unlocked!"}</div>
                <div style={{color:getTheme(themeUnlockPopup.id).accent,fontSize:14,fontWeight:600,marginBottom:8}}>{themeUnlockPopup.label}</div>
                <div style={{color:C.silver,fontSize:12,lineHeight:1.6,marginBottom:20}}>{nl?"Je hebt dit thema ontgrendeld door niveau te bereiken in spellen!":"You unlocked this theme by reaching a level in games!"}</div>
                <button onClick={()=>{setThemeId(themeUnlockPopup.id);setThemeUnlockPopup(null);}} style={{background:`linear-gradient(135deg,${getTheme(themeUnlockPopup.id).grad1},${getTheme(themeUnlockPopup.id).grad2})`,border:"none",borderRadius:12,padding:"12px 24px",color:"#0a1220",fontWeight:700,fontSize:14,cursor:"pointer",width:"100%"}}>
                  {nl?"Thema activeren":"Activate theme"}
                </button>
              </div>
            </div>,
            document.body
          )}
          <div style={s_GC()}>
            <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>THEME</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {themes.map(th=>{
                const tObj=getTheme(th.id);
                const isA=themeId===th.id;
                const premLocked=th.premium&&license==='freemium';
                const reqLvl=th.gameLvl||0;
                const gameLocked=reqLvl>0&&maxGameLevel<reqLvl;
                const locked=premLocked||gameLocked;
                const justUnlocked=reqLvl>0&&maxGameLevel>=reqLvl;
                return(
                  <button key={th.id} onClick={()=>{
                    if(premLocked) return;
                    if(gameLocked) return;
                    playSound('click');setThemeId(th.id);
                  }} style={{background:isA?`${tObj.accent}18`:tObj.bg4,border:`1.5px solid ${isA?tObj.accent:tObj.cardBorderBot}`,borderRadius:12,padding:"10px",cursor:locked?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:8,opacity:locked?0.5:1,position:"relative"}}>
                    <div style={{width:22,height:22,borderRadius:"50%",background:th.grad,flexShrink:0}}/>
                    <span style={{color:isA?tObj.accent:C.silver,fontSize:12,fontWeight:isA?700:400,flex:1,textAlign:"left"}}>{th.icon} {th.label}</span>
                    {premLocked&&<span style={{fontSize:10,color:C.orange}}>🔒</span>}
                    {gameLocked&&<div style={{fontSize:9,color:"#C8A84B",textAlign:"right",lineHeight:1.3}}>🎮<br/>Lvl {reqLvl}</div>}
                    {isA&&!locked&&<span style={{color:tObj.accent,fontSize:12}}>✓</span>}
                    {justUnlocked&&!isA&&!locked&&<span style={{fontSize:9,color:"#4AC87A",fontWeight:700}}>✨ NEW</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 5 · Sound theme */}
          <SoundThemePicker lang={lang} license={license} maxGameLevel={maxGameLevel} />

          {/* 6 · App settings (language, week reset) */}
          <div style={s_GC()}>
            <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>{t("dash.settings_heading",lang)}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${_T.cardBorderBot}`}}>
              <span style={{color:C.white,fontSize:13}}>{t("dash.language",lang)}</span>
              <button onClick={onOpenLangPicker} style={{background:`${_T.accent}15`,border:`1px solid ${_T.accent}55`,color:_T.accent,borderRadius:8,padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                {(LANGUAGES.find(l=>l.code===lang)||LANGUAGES[0]).flag} {lang.toUpperCase()} ▾
              </button>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0"}}>
              <span style={{color:C.white,fontSize:13}}>🔄 Reset week</span>
              <button style={{background:"transparent",border:`1px solid ${C.red}44`,color:C.red,borderRadius:8,padding:"5px 12px",cursor:"pointer",fontSize:12}} onClick={onResetWeek}>Reset</button>
            </div>
          </div>

          {/* 7 · Notifications & push settings */}
          <div style={s_GC({marginBottom:0})}>
            <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>{t("dash.notifications_heading",lang)}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${_T.cardBorderBot}`}}>
              <div>
                <div style={{color:C.white,fontSize:13}}>🔔 {t("push.notifs",lang)}</div>
                <div style={{color:C.silver,fontSize:11,marginTop:2}}>
                  {notifPerm==="denied"?t("push.blocked",lang):notifPerm!=="granted"?t("push.not_enabled",lang):pushEnabled?t("push.enabled",lang):(nl?"Uitgeschakeld":"Disabled")}
                </div>
              </div>
              {notifPerm==="denied"&&<span style={{color:C.red,fontSize:11}}>{t("push.allow_browser",lang)}</span>}
              {notifPerm!=="granted"&&notifPerm!=="denied"&&<button onClick={enablePush} style={{background:`${_T.accent}20`,border:`1px solid ${_T.accent}`,color:_T.accent,borderRadius:8,padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>{t("push.enable_btn",lang)}</button>}
              {notifPerm==="granted"&&<button onClick={togglePush} style={{position:"relative",width:48,height:26,borderRadius:13,border:"none",cursor:"pointer",background:pushEnabled?C.teal:_T.bg4,transition:"background 0.2s",padding:0}}>
                <span style={{position:"absolute",top:3,left:pushEnabled?24:3,width:20,height:20,borderRadius:"50%",background:C.white,transition:"left 0.2s",display:"block"}}/>
              </button>}
            </div>
            {pushSubMissing && notifPerm==="granted" && pushEnabled && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",margin:"8px 0",background:`${C.orange}18`,border:`1px solid ${C.orange}44`,borderRadius:10}}>
                <span style={{color:C.orange,fontSize:12}}>{nl?"⚠ Verbinding verbroken. Klik om opnieuw te verbinden.":"⚠ Subscription lost. Tap to reconnect."}</span>
                <button onClick={async()=>{await subscribeToPush();setPushSubMissing(false);}} style={{background:`${C.orange}22`,border:`1px solid ${C.orange}`,color:C.orange,borderRadius:8,padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,flexShrink:0,marginLeft:8}}>
                  {nl?"Herstel":"Fix"}
                </button>
              </div>
            )}
            <div style={{padding:"12px 0",borderBottom:`1px solid ${_T.cardBorderBot}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{color:C.white,fontSize:13}}>⏰ {t("push.remind_before",lang)}</span>
                <span style={{color:_T.accent,fontSize:12,fontWeight:700}}>{reminderOffset} min</span>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {[0,5,10,15,30,60].map(v=>(
                  <button key={v} onClick={()=>{setReminderOffset(v);saveReminderPrefs(v,tzOffset);}} style={{background:reminderOffset===v?`${_T.accent}20`:_T.bg4,border:`1px solid ${reminderOffset===v?_T.accent:_T.cardBorderBot}`,color:reminderOffset===v?_T.accent:C.silver,borderRadius:20,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:reminderOffset===v?700:400}}>
                    {v===0?t("dash.at_time",lang):`${v}m`}
                  </button>
                ))}
              </div>
            </div>
            <div style={{padding:"12px 0"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{color:C.white,fontSize:13}}>🌍 {t("dash.timezone",lang)}</span>
                <span style={{color:_T.accent,fontSize:12,fontWeight:700}}>UTC+{tzOffset/60}</span>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {[-60,0,60,120,180,240,300,360].map(v=>(
                  <button key={v} onClick={()=>{setTzOffset(v);saveReminderPrefs(reminderOffset,v);}} style={{background:tzOffset===v?`${_T.accent}20`:_T.bg4,border:`1px solid ${tzOffset===v?_T.accent:_T.cardBorderBot}`,color:tzOffset===v?_T.accent:C.silver,borderRadius:20,padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:tzOffset===v?700:400}}>
                    {v>=0?`+${v/60}`:`${v/60}`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 8 · Danger zone — always last */}
          <div style={s_GC({marginBottom:14,border:`1px solid ${C.red}33`})}>
            <div style={{fontSize:13,color:C.red,letterSpacing:3,fontWeight:600,marginBottom:12}}>{nl?"ACCOUNT VERWIJDEREN":"DELETE ACCOUNT"}</div>
            {!acctDeleteConfirm ? (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:C.silver,fontSize:12,lineHeight:1.4,flex:1,marginRight:12}}>{nl?"Dit verwijdert je account en alle gegevens permanent.":"This permanently deletes your account and all data."}</span>
                <button onClick={()=>setAcctDeleteConfirm(true)} style={{background:`${C.red}15`,border:`1px solid ${C.red}55`,color:C.red,borderRadius:8,padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600,flexShrink:0}}>
                  {nl?"Verwijder":"Delete"}
                </button>
              </div>
            ) : (
              <div>
                <div style={{color:C.white,fontSize:13,fontWeight:600,marginBottom:6}}>⚠️ {nl?"Weet je het zeker?":"Are you sure?"}</div>
                <div style={{color:C.silver,fontSize:12,marginBottom:14,lineHeight:1.5}}>
                  {nl?"Al je berichten, scores, vrienden en gegevens worden permanent verwijderd. Dit kan niet ongedaan worden gemaakt.":"All your posts, scores, friends, and data will be permanently deleted. This cannot be undone."}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setAcctDeleteConfirm(false)} style={{flex:1,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:8,padding:"8px",cursor:"pointer",fontSize:12}}>
                    {nl?"Annuleer":"Cancel"}
                  </button>
                  <button
                    disabled={acctDeleteLoading}
                    onClick={async()=>{
                      setAcctDeleteLoading(true);
                      try {
                        await deleteAccount(currentUserId);
                        localStorage.clear();
                        window.location.reload();
                      } catch {
                        setAcctDeleteLoading(false);
                        setAcctDeleteConfirm(false);
                      }
                    }}
                    style={{flex:1,background:C.red,border:"none",color:"#fff",borderRadius:8,padding:"8px",cursor:acctDeleteLoading?"not-allowed":"pointer",fontSize:12,fontWeight:700,opacity:acctDeleteLoading?0.6:1}}
                  >
                    {acctDeleteLoading?(nl?"Verwijderen...":"Deleting..."):(nl?"Ja, verwijder alles":"Yes, delete everything")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <PhotoLightbox src={lightboxSrc} onClose={()=>setLightboxSrc(null)}/>
    </div>
    </>
  );
}

export default Dashboard;
